/**
 * SlotMachineModal — Interactive 3-Reel Cyber Slot Machine Interface.
 * 
 * Flow:
 * 1. Cabinet presentation with ticket display & lever / spin button
 * 2. 3 vertical spinning reels with mechanical blur and staggered stops:
 *    - Reel 1: Unit (1.0s)
 *    - Reel 2: Component (bullet-color) (1.6s)
 *    - Reel 3: Style (REEL_3_STYLES.length) (2.3s)
 * 3. Outcome presentation (New unlock vs. Duplicate 100 Fugazi refund)
 */

import { slotMachine, REEL_3_STYLES, REEL_2_ATTRIBUTES, PLAYER_REEL_ENTRY } from '../engine/slotMachine.js';
import { wallet } from '../engine/wallet.js';
import { inventory } from '../engine/inventory.js';
import { hubNavigator } from './hubNavigator.js';
import { getCardThumbUrl } from '../data/cardThumbUrl.js';
import { CARDS_DATA } from '../data/cards.js';
import { buildSpriteBitmap, buildAuraBitmap } from '../engine/render/unitSprites.js';
import { ensureSlotIconDefs, getUnitRarityFrameSvg, getReel3CylinderSvg } from './slotIcons.js';
import {
  drawProcessingPillarRig,
  PRESERVER_LOCK_WALK_FRAMES,
  PRESERVER_LOCK_BODY_FRAMES,
  getPreserverLockCoronaCanvas
} from '../engine/render/units.js';

// Sul Procedural Tentacle Physics Constants & Scratch Buffers (Supply Surge Holo-Stage)
const DELVING_TENTACLE_FREQ = Object.freeze([0.85, 1.18, 0.92, 1.28, 0.88, 1.12, 1.22, 0.96, 1.15]);
const DELVING_TENTACLE_PHASE = Object.freeze([0.0, 1.85, 3.70, 0.95, 4.80, 2.30, 5.40, 1.40, 3.10]);
const DELVING_TENTACLE_SPATIAL = Object.freeze([4.8, 5.6, 5.0, 6.2, 5.4, 5.8, 6.5, 5.2, 6.0]);
const DELVING_TENTACLE_LEN = Object.freeze([0.72, 0.92, 0.78, 1.05, 1.25, 1.00, 0.85, 1.08, 0.80]);
const DELVING_TENTACLE_AMP = Object.freeze([1.15, 0.92, 1.08, 1.30, 1.00, 1.20, 1.35, 1.10, 1.25]);
const DELVING_TENTACLE_CURL_FREQ = Object.freeze([1.9, 2.5, 1.7, 2.9, 2.1, 2.6, 3.2, 2.3, 2.8]);
const DELVING_TENTACLE_CURL_AMP = Object.freeze([3.2, 4.0, 3.5, 5.2, 4.5, 4.8, 6.0, 4.2, 5.5]);
const DELVING_TENTACLE_ANCHOR_X = Object.freeze([-0.28, -0.22, -0.16, -0.24, -0.18, -0.10, -0.20, -0.14, -0.06]);
const DELVING_TENTACLE_ANCHOR_Y = Object.freeze([0.10, 0.14, 0.12, 0.16, 0.20, 0.18, 0.22, 0.25, 0.23]);
const DELVING_TENTACLE_BG_COLORS = Object.freeze([
  '#2a0404', '#3f0808', '#450a0a', '#581c87', '#6b1212', '#7f1d1d', '#991b1b', '#7f1d1d', '#450a0a'
]);
const DELVING_TENTACLE_FG_COLORS = Object.freeze([
  '#450a0a', '#6b1212', '#7f1d1d', '#991b1b', '#b91c1c', '#dc2626', '#ef4444', '#dc2626', '#b91c1c'
]);
const DELVING_TENTACLE_SEG_WIDTHS = Object.freeze([3.6, 3.2, 2.8, 2.4, 2.0, 1.7, 1.4, 1.1, 0.9]);
const _slotDelvingTentaclesScratch = Array.from({ length: 9 }, () => Array.from({ length: 10 }, () => ({ x: 0, y: 0 })));

const PROCESSING_ARM_ANCHOR_OFFSETS = Object.freeze([
  { dx: -0.16, dy: 0.18 },
  { dx: 0.22, dy: -0.05 },
  { dx: 0.26, dy: 0.10 },
  { dx: 0.18, dy: 0.26 },
  { dx: -0.10, dy: 0.30 }
]);
const PROCESSING_FEELER_ROOTS = Object.freeze([
  { dx: -0.22, dy: 0.36 },
  { dx: -0.08, dy: 0.38 },
  { dx: 0.08, dy: 0.38 },
  { dx: 0.20, dy: 0.35 }
]);
const _slotProcessingFeelersScratch = Array.from({ length: 4 }, () => Array.from({ length: 7 }, () => ({ x: 0, y: 0 })));

let _cachedSlotFaceGrad = null;
let _cachedSlotGlassGrad = null;
const _cachedSlotFloorGradMap = new Map();
const _cachedSlotBannerGradMap = new Map();

export class SlotMachineModal {
  constructor(audioEngine = null, packOpeningModal = null, game = null) {
    this.audio = audioEngine;
    this.packOpeningModal = packOpeningModal;
    this.game = game;

    this.modalEl = document.getElementById('slot-machine-modal');
    this.stageContainer = document.getElementById('slot-modal-stage');
    this.btnClose = document.getElementById('btn-close-slot-modal');
    this.btnBackdrop = document.getElementById('slot-modal-backdrop');

    this._isSpinning = false;
    this._isOutcomeActive = false;
    this._lastResult = null;
    this._cachedItemHeight = null;
    this._outcomeTimer = null;
    this._holoAnimFrame = null;
    this._imgCache = new Map();
    this._boundDismissOutcome = (e) => this.dismissOutcome(e);
    this._boundDismissKey = (e) => this._onDismissKey(e);

    // Auto Turbo State & Timers
    this._autoTurboActive = false;
    this._autoTurboStopping = false;
    this._isTurboSpin = false;
    this._spinTimers = [];
    this._autoNextTimer = null;
    this._reelStopStates = [false, false, false];
    this._activeSpinHandlers = null;

    this.init();
  }

  _clearSpinTimers() {
    for (let i = 0; i < this._spinTimers.length; i++) {
      clearTimeout(this._spinTimers[i]);
    }
    this._spinTimers = [];
  }

  _clearAutoNextTimer() {
    if (this._autoNextTimer) {
      clearTimeout(this._autoNextTimer);
      this._autoNextTimer = null;
    }
  }

  init() {
    window.addEventListener('resize', () => {
      this._cachedItemHeight = null;
    });

    if (this.btnClose) {
      this.btnClose.addEventListener('click', () => this.close());
    }
    if (this.btnBackdrop) {
      this.btnBackdrop.addEventListener('click', () => this.close());
    }

    wallet.addListener(() => {
      if (this.isOpen() && !this._isSpinning && !this._autoTurboActive) {
        this.updateTicketHeader();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen() && !this._isSpinning) {
        if (this._autoTurboActive) {
          this._autoTurboActive = false;
          this._autoTurboStopping = false;
          this._clearSpinTimers();
          this._clearAutoNextTimer();
          this.updateTicketHeader();
        }
        if (this.game?.directivesModal?.isOpen?.()) return;
        e.stopPropagation();
        if (this._isOutcomeActive) {
          this.dismissOutcome(e);
          return;
        }
        hubNavigator.navigateTo('archive');
      }
    });
  }

  open() {
    if (!this.modalEl) return;
    this.game?.pauseForMenu?.('slotMachine');
    this.modalEl.classList.remove('hidden');
    this.modalEl.classList.add('active');
    hubNavigator.setActiveDeck('matrix');
    this.renderCabinet();
  }

  close() {
    if (this._isSpinning || !this.modalEl) return;
    this._autoTurboActive = false;
    this._autoTurboStopping = false;
    this._clearSpinTimers();
    this._clearAutoNextTimer();
    if (this._isOutcomeActive) {
      this.dismissOutcome();
    }
    if (this._holoAnimFrame) {
      cancelAnimationFrame(this._holoAnimFrame);
      this._holoAnimFrame = null;
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    const wasOpen = this.isOpen();
    this.modalEl.classList.remove('active');
    this.modalEl.classList.add('hidden');
    if (wasOpen) {
      this.game?.resumeForMenu?.('slotMachine');
    }
  }

  isOpen() {
    return this.modalEl && this.modalEl.classList.contains('active');
  }

  _attachArsenalListener(btnArsenal) {
    if (!btnArsenal || btnArsenal._hasArsenalListener) return;
    btnArsenal._hasArsenalListener = true;
    let handled = false;
    const onArsenal = (e) => {
      if (handled) return;
      handled = true;
      setTimeout(() => { handled = false; }, 300);
      e.preventDefault();
      this.close();
      hubNavigator.navigateTo('arsenal');
    };
    btnArsenal.addEventListener('click', onArsenal);
    btnArsenal.addEventListener('touchend', onArsenal);
  }

  updateTicketHeader() {
    const btnSpin = document.getElementById('btn-spin-slots');
    let btnArsenal = document.getElementById('btn-arsenal-slots');
    const tickets = wallet.getTickets();
    const hasTickets = tickets > 0;

    if (btnSpin) {
      if (this._autoTurboActive || this._autoTurboStopping) {
        btnSpin.disabled = this._autoTurboStopping;
        btnSpin.className = `hud-btn slot-btn-spin slot-btn-stop-auto ${this._autoTurboStopping ? 'stopping disabled' : ''}`.trim();
        const label = this._autoTurboStopping ? 'STOPPING' : 'STOP AUTO';
        btnSpin.innerHTML = `<span class="spin-btn-label">${label}</span>` +
          (this._isOutcomeActive && !this._autoTurboStopping ? '<div class="auto-turbo-progress-bar"></div>' : '');
        if (btnArsenal) {
          btnArsenal.classList.add('hidden');
          btnArsenal.style.display = 'none';
        }
        return;
      }

      if (this._isSpinning) {
        btnSpin.disabled = false;
        btnSpin.className = 'hud-btn slot-btn-spin slot-btn-auto-turbo';
        btnSpin.innerHTML = '<span class="spin-btn-label">AUTO TURBO</span>';
        if (btnArsenal) {
          btnArsenal.classList.add('hidden');
          btnArsenal.style.display = 'none';
        }
        return;
      }

      btnSpin.disabled = !hasTickets;
      btnSpin.className = `hud-btn slot-btn-spin ${!hasTickets ? 'disabled' : ''}`;
      btnSpin.innerHTML = `<span class="spin-ticket-badge">${tickets}x</span> <span class="spin-btn-label">SPIN</span>`;
    }

    if (!btnArsenal && btnSpin && btnSpin.parentElement) {
      btnArsenal = document.createElement('button');
      btnArsenal.id = 'btn-arsenal-slots';
      btnArsenal.className = 'hud-btn slot-btn-arsenal';
      btnArsenal.textContent = 'Arsenal';
      btnSpin.parentElement.appendChild(btnArsenal);
      this._attachArsenalListener(btnArsenal);
    }

    if (btnArsenal) {
      this._attachArsenalListener(btnArsenal);
      if (!hasTickets && !this._isSpinning && !this._autoTurboActive) {
        btnArsenal.classList.remove('hidden');
        btnArsenal.style.display = '';
      } else {
        btnArsenal.classList.add('hidden');
        btnArsenal.style.display = 'none';
      }
    }
  }

  // Reel item templates render 3x the actual pool, back to back - the
  // window shows 3 rows at once (see .slot-reels-window in main.css), so
  // whatever index lands in the *middle* copy always has a real neighbor
  // above and below, including at the pool's own start/end (the "endless
  // drum" trick - no synthetic blank rows to special-case at the edges).
  _renderReelItems(items, template) {
    return [...items, ...items, ...items].map(template).join('');
  }

  renderCabinet() {
    if (!this.stageContainer) return;
    ensureSlotIconDefs();

    const tickets = wallet.getTickets();
    const reel1Items = slotMachine.getReel1Items();
    const canSpin = tickets > 0;

    this.stageContainer.innerHTML = `
      <div class="slot-cabinet">
        <div class="modal-scifi-header slot-header" id="slot-header-jumbotron">
          <span class="scifi-header-badge slot-badge" id="slot-header-badge">TEST YOUR LUCK</span>
          <h2 class="scifi-header-title slot-title" id="slot-header-title">SUPPLY SURGE</h2>
          <div class="scifi-header-beam slot-beam" id="slot-header-beam"></div>
        </div>

        <div class="slot-stage-content">
          <!-- The 3 Cyber Reels Display - 3 rows visible per reel, middle row is the payline -->
          <div class="slot-reels-frame" id="slot-reels-frame">
            <div class="slot-frame-bolt bolt-tl">
              <svg viewBox="0 0 30 30" fill="none">
                <path d="M1 26V8L8 1H26L22 5H10L5 10V22L1 26Z" fill="url(#metal-hull)" stroke="#0f172a" stroke-width="1"/>
                <polygon points="10.5,6.5 14.5,6.5 16.5,10 14.5,13.5 10.5,13.5 8.5,10" fill="url(#dark-steel)" stroke="url(#silver-hull)" stroke-width="0.9"/>
                <line x1="10.5" y1="10" x2="14.5" y2="10" stroke="#94a3b8" stroke-width="1.1" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="slot-frame-bolt bolt-tr">
              <svg viewBox="0 0 30 30" fill="none">
                <path d="M1 26V8L8 1H26L22 5H10L5 10V22L1 26Z" fill="url(#metal-hull)" stroke="#0f172a" stroke-width="1"/>
                <polygon points="10.5,6.5 14.5,6.5 16.5,10 14.5,13.5 10.5,13.5 8.5,10" fill="url(#dark-steel)" stroke="url(#silver-hull)" stroke-width="0.9"/>
                <line x1="10.5" y1="10" x2="14.5" y2="10" stroke="#94a3b8" stroke-width="1.1" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="slot-frame-bolt bolt-bl">
              <svg viewBox="0 0 30 30" fill="none">
                <path d="M1 26V8L8 1H26L22 5H10L5 10V22L1 26Z" fill="url(#metal-hull)" stroke="#0f172a" stroke-width="1"/>
                <polygon points="10.5,6.5 14.5,6.5 16.5,10 14.5,13.5 10.5,13.5 8.5,10" fill="url(#dark-steel)" stroke="url(#silver-hull)" stroke-width="0.9"/>
                <line x1="10.5" y1="10" x2="14.5" y2="10" stroke="#94a3b8" stroke-width="1.1" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="slot-frame-bolt bolt-br">
              <svg viewBox="0 0 30 30" fill="none">
                <path d="M1 26V8L8 1H26L22 5H10L5 10V22L1 26Z" fill="url(#metal-hull)" stroke="#0f172a" stroke-width="1"/>
                <polygon points="10.5,6.5 14.5,6.5 16.5,10 14.5,13.5 10.5,13.5 8.5,10" fill="url(#dark-steel)" stroke="url(#silver-hull)" stroke-width="0.9"/>
                <line x1="10.5" y1="10" x2="14.5" y2="10" stroke="#94a3b8" stroke-width="1.1" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="slot-reels-window payline-locked" id="slot-reels-window">
              <div class="slot-payline-band"></div>
              <div class="slot-payline-marker payline-left">
                <svg viewBox="0 0 24 36" fill="none">
                  <polygon points="0,2 7,6 12,12 12,24 7,30 0,34" fill="url(#dark-steel)" stroke="url(#metal-hull)" stroke-width="1.1" stroke-linejoin="round"/>
                  <polygon points="3,8 21,18 3,28 6.5,18" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.95" stroke-linejoin="round"/>
                  <polygon points="6,13.5 16.5,18 6,22.5 8,18" fill="#ffffff"/>
                </svg>
              </div>
              <div class="slot-payline-marker payline-right">
                <svg viewBox="0 0 24 36" fill="none">
                  <polygon points="0,2 7,6 12,12 12,24 7,30 0,34" fill="url(#dark-steel)" stroke="url(#metal-hull)" stroke-width="1.1" stroke-linejoin="round"/>
                  <polygon points="3,8 21,18 3,28 6.5,18" fill="url(#gold-trim)" stroke="#fef08a" stroke-width="0.95" stroke-linejoin="round"/>
                  <polygon points="6,13.5 16.5,18 6,22.5 8,18" fill="#ffffff"/>
                </svg>
              </div>

              <!-- Reel 1: Unit (or "Spieler" for the global Card Frame category) -->
              <div class="slot-reel" id="slot-reel-1">
                <div class="reel-strip" id="reel-strip-1">
                  ${this._renderReelItems(reel1Items, u => u.isPlayer ? `
                    <div class="reel-item reel-player-item">
                      <span class="reel-item-icon">${u.icon}</span>
                      <span class="reel-item-name">${u.name}</span>
                    </div>
                  ` : `
                    <div class="reel-item reel-unit-item" data-rarity="${(u.rarity || 'Common').toLowerCase()}">
                      <div class="reel-unit-frame-wrap">
                        <img src="${getCardThumbUrl(u.image, 's')}" class="reel-item-art" alt="${u.name}" onerror="this.style.visibility='hidden'">
                        ${getUnitRarityFrameSvg(u.rarity)}
                      </div>
                      <span class="reel-item-name">${u.name}</span>
                    </div>
                  `)}
                </div>
              </div>

              <div class="slot-reel-divider"></div>

              <!-- Reel 2: Component (bullet-color / frame) -->
              <div class="slot-reel" id="slot-reel-2">
                <div class="reel-strip" id="reel-strip-2">
                  ${this._renderReelItems(REEL_2_ATTRIBUTES, a => `
                    <div class="reel-item reel-attr-item">
                      <span class="reel-item-icon">${a.icon}</span>
                      <span class="reel-item-name">${a.name}</span>
                    </div>
                  `)}
                </div>
              </div>

              <div class="slot-reel-divider"></div>

              <!-- Reel 3: Value / Style (REEL_3_STYLES.length) -->
              <div class="slot-reel" id="slot-reel-3">
                <div class="reel-strip" id="reel-strip-3">
                  ${this._renderReelItems(REEL_3_STYLES, s => `
                    <div class="reel-item reel-style-item" data-style-category="${s.category}" style="--style-color: ${s.hex}">
                      <span class="reel-style-swatch">${getReel3CylinderSvg(s.hex, s.category)}</span>
                      <span class="reel-item-name">${s.name}</span>
                    </div>
                  `)}
                </div>
              </div>
            </div>

            <!-- Centered Holo-Stage Canvas Overlay (TV & Arcade Presentation) -->
            <canvas class="slot-holo-canvas hidden" id="slot-holo-canvas"></canvas>

            <!-- Backwards-compatible dummy container for legacy selectors -->
            <div class="slot-result-tray hidden" id="slot-result-tray"></div>
          </div>

          <div class="slot-footer-zone">
            <div class="slot-actions">
              <button id="btn-spin-slots" class="hud-btn slot-btn-spin ${!canSpin ? 'disabled' : ''}" ${!canSpin ? 'disabled' : ''}>
                <span class="spin-ticket-badge">${tickets}x</span> <span class="spin-btn-label">SPIN</span>
              </button>
              <button id="btn-arsenal-slots" class="hud-btn slot-btn-arsenal ${canSpin ? 'hidden' : ''}" style="${canSpin ? 'display: none;' : ''}">
                Arsenal
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    const btnSpin = document.getElementById('btn-spin-slots');
    if (btnSpin) {
      btnSpin.addEventListener('click', (e) => this._handleSpinBtnClick(e));
    }

    const btnArsenal = document.getElementById('btn-arsenal-slots');
    if (btnArsenal) {
      this._attachArsenalListener(btnArsenal);
    }

    const holoCanvas = document.getElementById('slot-holo-canvas');
    if (holoCanvas) {
      holoCanvas.addEventListener('click', (e) => {
        if (this._isOutcomeActive) this.dismissOutcome(e);
      });
    }



    // Apply 3D drum vanishing-point perspective to the initially visible rows
    this._updateAllReelRowPerspectives(
      this._lastResult?.unitIndex ?? 0,
      this._lastResult?.attributeIndex ?? 0,
      this._lastResult?.styleIndex ?? 0
    );

    const reelsWindow = document.getElementById('slot-reels-window');
    if (reelsWindow && typeof ResizeObserver !== 'undefined') {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
      }
      this._resizeObserver = new ResizeObserver(() => {
        if (this._isSpinning) {
          this._pendingHeightRecalc = true;
          return;
        }
        this._cachedItemHeight = null;
      });
      this._resizeObserver.observe(reelsWindow);
    }
  }

  _getItemHeight() {
    if (this._cachedItemHeight) return this._cachedItemHeight;
    const reelsWindow = typeof document !== 'undefined' ? document.getElementById('slot-reels-window') : null;
    if (reelsWindow && reelsWindow.offsetHeight > 0) {
      // Used (fractional) height, not offsetHeight: clamp()/cqh row heights are
      // fractional and offsetHeight rounds, an error that stopOffset multiplies by
      // the row index. getComputedStyle is also immune to ancestor transforms,
      // unlike getBoundingClientRect.
      const usedH = parseFloat(getComputedStyle(reelsWindow).height);
      this._cachedItemHeight = (Number.isFinite(usedH) && usedH > 0 ? usedH : reelsWindow.offsetHeight) / 3;
      return this._cachedItemHeight;
    }
    const isLandscapeMobile = typeof window !== 'undefined' && window.innerHeight <= 500 && window.innerWidth > window.innerHeight;
    const isMobile = (typeof window !== 'undefined' && window.innerWidth <= 768) || isLandscapeMobile;
    this._cachedItemHeight = isMobile ? 92 : 140;
    return this._cachedItemHeight;
  }

  _handleSpinBtnClick(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // 1. In Auto-Turbo Mode (either spinning or viewing 3s outcome): button shows "STOP AUTO"
    if (this._autoTurboActive) {
      if (this._isSpinning) {
        // Player clicks "STOP AUTO" while reels are spinning:
        // Rule: "laufender spin noch zu ende"
        this._autoTurboStopping = true;
        this._autoTurboActive = false;
        this.updateTicketHeader();
        return;
      }
      if (this._isOutcomeActive) {
        // Player clicks "STOP AUTO" during the 3-second outcome showcase:
        this._autoTurboActive = false;
        this._autoTurboStopping = false;
        this._clearAutoNextTimer();
        if (this._outcomeTimer) {
          clearTimeout(this._outcomeTimer);
          this._outcomeTimer = null;
        }
        this.updateTicketHeader();
        return;
      }
    }

    // 2. Normal Spin currently running: button shows "AUTO TURBO"
    if (this._isSpinning) {
      // Player clicks "AUTO TURBO" during spin!
      this._autoTurboActive = true;
      this._autoTurboStopping = false;
      this.updateTicketHeader();
      this._accelerateCurrentSpin();
      return;
    }

    // 3. Outcome active in manual idle state: clicking dismisses it
    if (this._isOutcomeActive) {
      this.dismissOutcome();
    }

    // 4. Idle state: start a new spin (or navigate to cargo if no tickets)
    if (wallet.getTickets() > 0) {
      this.executeSpin(false);
    } else {
      hubNavigator.navigateTo('cargo');
    }
  }

  _accelerateCurrentSpin() {
    if (!this._isSpinning || !this._activeSpinHandlers) return;
    this._clearSpinTimers();
    this._isTurboSpin = true;
    const { stopReel1, stopReel2, stopReel3, finishSpin } = this._activeSpinHandlers;

    let delay = 30;
    if (!this._reelStopStates[0]) {
      this._spinTimers.push(setTimeout(stopReel1, delay));
      delay += 160;
    }
    if (!this._reelStopStates[1]) {
      this._spinTimers.push(setTimeout(stopReel2, delay));
      delay += 160;
    }
    if (!this._reelStopStates[2]) {
      this._spinTimers.push(setTimeout(() => {
        stopReel3();
        this._spinTimers.push(setTimeout(finishSpin, 180));
      }, delay));
    } else {
      this._spinTimers.push(setTimeout(finishSpin, 120));
    }
  }

  executeSpin(isTurbo = false) {
    if (this._isSpinning) return;
    if (this._isOutcomeActive) {
      this.dismissOutcome();
    }
    if (!slotMachine.canSpin()) {
      this._autoTurboActive = false;
      this._autoTurboStopping = false;
      this.updateTicketHeader();
      return;
    }

    this._clearSpinTimers();
    this._clearAutoNextTimer();

    this._isSpinning = true;
    this._isTurboSpin = Boolean(isTurbo || this._autoTurboActive);
    this._reelStopStates = [false, false, false];

    // Deduct ticket & get random outcome from engine
    const result = slotMachine.spin();
    this._lastResult = result;
    this.updateTicketHeader();

    const strip1 = document.getElementById('reel-strip-1');
    const strip2 = document.getElementById('reel-strip-2');
    const strip3 = document.getElementById('reel-strip-3');
    const reelEl1 = document.getElementById('slot-reel-1');
    const reelEl2 = document.getElementById('slot-reel-2');
    const reelEl3 = document.getElementById('slot-reel-3');
    const reelsWindow = document.getElementById('slot-reels-window');

    if (reelsWindow) reelsWindow.classList.remove('payline-locked');
    [reelEl1, reelEl2, reelEl3].forEach(r => r && r.classList.remove('reel-lock-flash'));

    const triggerLockFlash = (reelEl) => {
      if (!reelEl) return;
      reelEl.classList.remove('reel-lock-flash');
      void reelEl.offsetWidth;
      reelEl.classList.add('reel-lock-flash');
    };

    // Remove row perspective classes while spinning
    [strip1, strip2, strip3].forEach(strip => {
      if (!strip) return;
      if (strip._activeRows) {
        for (let i = 0; i < strip._activeRows.length; i++) {
          const el = strip._activeRows[i];
          if (el) el.classList.remove('reel-row-top', 'reel-row-center', 'reel-row-bottom');
        }
        strip._activeRows = null;
      } else {
        strip.querySelectorAll('.reel-item').forEach(el => {
          el.classList.remove('reel-row-top', 'reel-row-center', 'reel-row-bottom');
        });
      }
    });

    // Read the actual on-screen row height (140px desktop / 92px mobile)
    const itemHeight = this._getItemHeight();
    const stopOffset = (poolLength, index) => (poolLength + index - 1) * itemHeight;

    const reel1PoolLength = slotMachine.getReel1Items().length;
    const reel2PoolLength = REEL_2_ATTRIBUTES.length;
    const reel3PoolLength = REEL_3_STYLES.length;

    const stopReel1 = () => {
      if (this._reelStopStates[0]) return;
      this._reelStopStates[0] = true;
      if (strip1) {
        strip1.className = this._isTurboSpin ? 'reel-strip stopping-turbo' : 'reel-strip stopping';
        strip1.style.transform = `translateY(-${stopOffset(reel1PoolLength, result.unitIndex)}px)`;
        this._applyReelRowPerspective(strip1, reel1PoolLength, result.unitIndex);
      }
      triggerLockFlash(reelEl1);
      if (this.audio) {
        if (this.audio.playCasinoChipBet) this.audio.playCasinoChipBet();
        else this.audio.playBark?.('ui_click');
      }
    };

    const stopReel2 = () => {
      if (this._reelStopStates[1]) return;
      this._reelStopStates[1] = true;
      if (strip2) {
        strip2.className = this._isTurboSpin ? 'reel-strip stopping-turbo' : 'reel-strip stopping';
        strip2.style.transform = `translateY(-${stopOffset(reel2PoolLength, result.attributeIndex)}px)`;
        this._applyReelRowPerspective(strip2, reel2PoolLength, result.attributeIndex);
      }
      triggerLockFlash(reelEl2);
      if (this.audio) {
        if (this.audio.playCasinoChipBet) this.audio.playCasinoChipBet();
        else this.audio.playBark?.('ui_click');
      }
    };

    const stopReel3 = () => {
      if (this._reelStopStates[2]) return;
      this._reelStopStates[2] = true;
      if (strip3) {
        strip3.className = this._isTurboSpin ? 'reel-strip stopping-turbo' : 'reel-strip stopping';
        strip3.style.transform = `translateY(-${stopOffset(reel3PoolLength, result.styleIndex)}px)`;
        this._applyReelRowPerspective(strip3, reel3PoolLength, result.styleIndex);
      }
      triggerLockFlash(reelEl3);
      if (reelsWindow) reelsWindow.classList.add('payline-locked');

      if (this.audio) {
        this.audio.stopCasinoSpin?.();
        if (this.audio.playCasinoChipBet) this.audio.playCasinoChipBet();
        else this.audio.playBark?.('ui_click');
      }
    };

    const finishSpin = () => {
      this.presentOutcome(result);
      this._isSpinning = false;
      if (this._pendingHeightRecalc) {
        this._pendingHeightRecalc = false;
        this._cachedItemHeight = null;
      }
      if (this._autoTurboStopping) {
        this._autoTurboActive = false;
        this._autoTurboStopping = false;
      }
      this.updateTicketHeader();
    };

    this._activeSpinHandlers = {
      stopReel1,
      stopReel2,
      stopReel3,
      finishSpin
    };

    // Phase 1: Mechanical Anticipation Kickback (reels pull up 16px)
    if (strip1) strip1.className = 'reel-strip anticipating';
    if (strip2) strip2.className = 'reel-strip anticipating';
    if (strip3) strip3.className = 'reel-strip anticipating';

    if (this.audio) {
      if (this.audio.playCasinoSpin) this.audio.playCasinoSpin();
      else this.audio.playBark?.('ui_click');
    }

    const accelTime = this._isTurboSpin ? 50 : 160;
    const r1Time = this._isTurboSpin ? 240 : 1000;
    const r2Time = this._isTurboSpin ? 440 : 1850;
    const r3Time = this._isTurboSpin ? 640 : 2850;
    const lockStillness = this._isTurboSpin ? 180 : 700;

    // Phase 2: Rapid acceleration into high-speed blur spin
    this._spinTimers.push(setTimeout(() => {
      if (strip1 && !this._reelStopStates[0]) strip1.className = 'reel-strip spinning-fast';
      if (strip2 && !this._reelStopStates[1]) strip2.className = 'reel-strip spinning-fast';
      if (strip3 && !this._reelStopStates[2]) strip3.className = 'reel-strip spinning-fast';
    }, accelTime));

    // Staggered Reel Stops with mechanical overshoot bounce & 3D perspective sizing:
    this._spinTimers.push(setTimeout(() => {
      stopReel1();
    }, r1Time));

    this._spinTimers.push(setTimeout(() => {
      stopReel2();
    }, r2Time));

    this._spinTimers.push(setTimeout(() => {
      stopReel3();
      this._spinTimers.push(setTimeout(() => {
        finishSpin();
      }, lockStillness));
    }, r3Time));
  }

  _applyReelRowPerspective(stripEl, poolLength, targetIndex) {
    if (!stripEl) return;
    if (stripEl._activeRows) {
      for (let i = 0; i < stripEl._activeRows.length; i++) {
        const el = stripEl._activeRows[i];
        if (el) el.classList.remove('reel-row-top', 'reel-row-center', 'reel-row-bottom');
      }
    } else {
      const items = stripEl.querySelectorAll('.reel-item');
      for (let i = 0; i < items.length; i++) {
        items[i].classList.remove('reel-row-top', 'reel-row-center', 'reel-row-bottom');
      }
    }

    const items = stripEl.querySelectorAll('.reel-item');
    const centerDomIdx = poolLength + targetIndex;
    const topDomIdx = centerDomIdx - 1;
    const bottomDomIdx = centerDomIdx + 1;

    const topEl = items[topDomIdx];
    const centerEl = items[centerDomIdx];
    const bottomEl = items[bottomDomIdx];

    if (topEl) topEl.classList.add('reel-row-top');
    if (centerEl) centerEl.classList.add('reel-row-center');
    if (bottomEl) bottomEl.classList.add('reel-row-bottom');

    stripEl._activeRows = [topEl, centerEl, bottomEl];
  }

  _updateAllReelRowPerspectives(idx1 = 0, idx2 = 0, idx3 = 0) {
    const strip1 = document.getElementById('reel-strip-1');
    const strip2 = document.getElementById('reel-strip-2');
    const strip3 = document.getElementById('reel-strip-3');
    if (strip1) this._applyReelRowPerspective(strip1, slotMachine.getReel1Items().length, idx1);
    if (strip2) this._applyReelRowPerspective(strip2, REEL_2_ATTRIBUTES.length, idx2);
    if (strip3) this._applyReelRowPerspective(strip3, REEL_3_STYLES.length, idx3);
  }

  // The outcome badge normally shows the REEL_3_STYLES color that was
  // actually landed on (result.style). A 'taunt' result is the one
  // exception - see the REEL_3_STYLES doc comment in slotMachine.js - so it
  // shows the won taunt's own icon/label/color (result.taunt) instead,
  // otherwise the tray would show a swatch color unrelated to the reward.
  _outcomeBadgeHTML(result, glowSize) {
    if (result.attribute?.id === 'taunt' && result.taunt) {
      const t = result.taunt;
      return `
        <div class="outcome-style-badge" style="border-color: ${t.themeColor}; box-shadow: 0 0 ${glowSize}px ${t.themeColor}">
          <span class="style-dot taunt-outcome-dot">${t.icon}</span>
          <span class="style-name">${t.label}</span>
        </div>
      `;
    }
    if (result.attribute?.id === 'profile_artwork' && result.artwork) {
      const art = result.artwork;
      return `
        <div class="outcome-style-badge" style="border-color: #00f0ff; box-shadow: 0 0 ${glowSize}px rgba(0, 240, 255, 0.45)">
          <img class="style-dot" src="${getCardThumbUrl(art.image, 's')}" style="width: 26px; height: 26px; border-radius: 4px; object-fit: cover; display: inline-block;" />
          <span class="style-name">${art.name}</span>
        </div>
      `;
    }
    if (result.attribute?.id === 'deckbuilder_card' && result.deckbuilderTheme) {
      const theme = result.deckbuilderTheme;
      return `
        <div class="outcome-style-badge" style="border-color: #00f0ff; box-shadow: 0 0 ${glowSize}px rgba(0, 240, 255, 0.45)">
          <span class="style-dot" style="display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;">${getSlotCategorySvg('deckbuilder_card', 'mini')}</span>
          <span class="style-name">${theme.name}</span>
        </div>
      `;
    }
    if (result.attribute?.id === 'card_theme' && result.cardTheme) {
      const theme = result.cardTheme;
      return `
        <div class="outcome-style-badge" style="border-color: #38bdf8; box-shadow: 0 0 ${glowSize}px rgba(56, 189, 248, 0.45)">
          <span class="style-dot" style="display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;">${getSlotCategorySvg('card_theme', 'mini')}</span>
          <span class="style-name">${theme.name}</span>
        </div>
      `;
    }
    if (result.attribute?.id === 'deck_icon' && result.deckIcon) {
      const icon = result.deckIcon;
      const borderCol = icon.rarity === 'epic' ? '#f59e0b' : '#c084fc';
      return `
        <div class="outcome-style-badge" style="border-color: ${borderCol}; box-shadow: 0 0 ${glowSize}px ${borderCol}88">
          <span class="style-dot" style="display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;">${getSlotCategorySvg('deck_icon', 'mini')}</span>
          <span class="style-name">${icon.label}</span>
        </div>
      `;
    }
    return `
      <div class="outcome-style-badge" style="border-color: ${result.style.hex}; box-shadow: 0 0 ${glowSize}px ${result.style.hex}">
        <span class="style-dot" style="background-color: ${result.style.hex}"></span>
        <span class="style-name">${result.style.name}${result.style.category ? ` (${result.style.category})` : ''}</span>
      </div>
    `;
  }

  presentOutcome(result) {
    this._isOutcomeActive = true;

    // 0. Physical Chassis Rumble & EMP Energy Blast Audio Knall
    const reelsFrame = document.getElementById('slot-reels-frame');
    if (reelsFrame) {
      reelsFrame.classList.remove('slot-emp-shake');
      void reelsFrame.offsetWidth; // Force CSS animation reflow
      reelsFrame.classList.add('slot-emp-shake');
    }
    if (this.audio) {
      if (this.audio.playMeltFusionImpact) this.audio.playMeltFusionImpact();
      else if (this.audio.playConcentrationBurst) this.audio.playConcentrationBurst();
    }

    // 1. Dim the 3 background reels (Option A: Walzen-Dimm & Holo-Fokus)
    const reelsWindow = document.getElementById('slot-reels-window');
    if (reelsWindow) {
      reelsWindow.classList.add('reels-dimmed');
    }

    // 2. Morph Jumbotron Header
    const header = document.getElementById('slot-header-jumbotron') || document.querySelector('.slot-header');
    const badge = document.getElementById('slot-header-badge') || document.querySelector('.slot-badge');
    const title = document.getElementById('slot-header-title') || document.querySelector('.slot-title');

    if (title) {
      title.style.background = 'none';
      title.style.webkitBackgroundClip = 'initial';
      title.style.webkitTextFillColor = 'initial';
      title.style.filter = 'none';
    }

    if (result.isDuplicate) {
      if (header) {
        header.classList.remove('jumbotron-new');
        header.classList.add('jumbotron-duplicate');
      }
      if (badge) badge.textContent = '⬡ DUPLICATE DETECTED';
      if (title) title.innerHTML = `<span style="color:#ffcc00; text-shadow:0 0 24px #ffcc00;">+${result.refundAmount || 100} FUGAZI REFUND</span>`;

      if (this.audio) {
        setTimeout(() => {
          if (!this._isOutcomeActive) return;
          if (this.audio.playSlotRefund) this.audio.playSlotRefund();
          else if (this.audio.playCasinoWin) this.audio.playCasinoWin();
          else this.audio.playBark?.('ui_click');
        }, 220);
      }
    } else {
      if (header) {
        header.classList.remove('jumbotron-duplicate');
        header.classList.add('jumbotron-new');
      }

      if (result.attribute?.id === 'taunt' && result.taunt) {
        if (badge) badge.textContent = '★ COMMANDER TAUNT ★';
        const tColor = result.taunt.themeColor || '#00f0ff';
        const tText = (result.taunt.label || result.taunt.text || 'TAUNT').toUpperCase();
        if (title) title.innerHTML = `<span style="color:${tColor}; text-shadow:0 0 24px ${tColor}">"${tText}"</span>`;
      } else if (result.attribute?.id === 'profile_artwork' && result.artwork) {
        if (badge) badge.textContent = '★ PROFILE ARTWORK ★';
        if (title) title.innerHTML = `<span style="color:#00f0ff; text-shadow:0 0 24px #00f0ff">${result.artwork.name.toUpperCase()}</span>`;
      } else if (result.attribute?.id === 'deckbuilder_card' && result.deckbuilderTheme) {
        if (badge) badge.textContent = '★ DECK BUILDER CHASSIS ★';
        if (title) title.innerHTML = `<span style="color:#00f0ff; text-shadow:0 0 24px #00f0ff">${result.deckbuilderTheme.name.toUpperCase()}</span>`;
      } else if (result.attribute?.id === 'card_theme' && result.cardTheme) {
        if (badge) badge.textContent = '★ CARDS CHASSIS ★';
        if (title) title.innerHTML = `<span style="color:#38bdf8; text-shadow:0 0 24px #38bdf8">${result.cardTheme.name.toUpperCase()}</span>`;
      } else if (result.attribute?.id === 'deck_icon' && result.deckIcon) {
        const glowCol = result.deckIcon.rarity === 'epic' ? '#f59e0b' : '#c084fc';
        if (badge) badge.textContent = '★ DECK INSIGNIA ★';
        if (title) title.innerHTML = `<span style="color:${glowCol}; text-shadow:0 0 24px ${glowCol}">${result.deckIcon.label.toUpperCase()}</span>`;
      } else {
        const attrLabel = (result.attribute?.name || 'COSMETIC').toUpperCase();
        const styleColor = result.style?.hex || '#00f0ff';
        const styleName = (result.style?.name || '').toUpperCase();
        if (badge) badge.textContent = `★ ${attrLabel} ★`;
        if (title) title.innerHTML = `<span style="color:${styleColor}; text-shadow:0 0 24px ${styleColor}">${styleName}</span>`;
      }

      if (this.audio) {
        setTimeout(() => {
          if (!this._isOutcomeActive) return;
          if (this.audio.playSlotHoloReveal) this.audio.playSlotHoloReveal();
          else if (this.audio.playPowerUp) this.audio.playPowerUp();
          else this.audio.playBark?.('ui_click');
        }, 220);
      }
    }

    // 3. Start Holo-Canvas Stage
    this._startHoloStage(result);

    // 4. Timer: 3.0s Auto-Dismiss in Auto Turbo OR 10.0s in Normal Mode
    if (this._outcomeTimer) {
      clearTimeout(this._outcomeTimer);
      this._outcomeTimer = null;
    }
    const outcomeDuration = this._autoTurboActive ? 3000 : 10000;
    this._outcomeTimer = setTimeout(() => {
      this._onOutcomeTimeout();
    }, outcomeDuration);

    setTimeout(() => {
      if (this._isOutcomeActive) {
        window.addEventListener('pointerdown', this._boundDismissOutcome, { capture: true, once: true });
        window.addEventListener('keydown', this._boundDismissKey, { capture: true, once: true });
      }
    }, 40);
  }

  _onOutcomeTimeout() {
    if (!this._isOutcomeActive) return;
    const shouldContinue = this._autoTurboActive && !this._autoTurboStopping && wallet.getTickets() > 0;
    this.dismissOutcome();
    if (shouldContinue) {
      this.executeSpin(true);
    } else {
      this._autoTurboActive = false;
      this._autoTurboStopping = false;
      this.updateTicketHeader();
    }
  }

  dismissOutcome(e = null) {
    if (!this._isOutcomeActive) return;

    const clickedStopBtn = e && e.target && e.target.closest && e.target.closest('#btn-spin-slots');
    if (clickedStopBtn) {
      this._autoTurboActive = false;
      this._autoTurboStopping = false;
      this._clearAutoNextTimer();
      if (this._outcomeTimer) {
        clearTimeout(this._outcomeTimer);
        this._outcomeTimer = null;
      }
      this.updateTicketHeader();
      return;
    }

    const isManualScreenSkip = Boolean(e && (!e.key || e.key !== 'Escape'));

    if (e && e.key === 'Escape') {
      this._autoTurboActive = false;
      this._autoTurboStopping = false;
    }

    this._isOutcomeActive = false;

    if (this._outcomeTimer) {
      clearTimeout(this._outcomeTimer);
      this._outcomeTimer = null;
    }
    this._clearAutoNextTimer();

    window.removeEventListener('pointerdown', this._boundDismissOutcome, { capture: true });
    window.removeEventListener('keydown', this._boundDismissKey, { capture: true });

    if (this._holoAnimFrame) {
      cancelAnimationFrame(this._holoAnimFrame);
      this._holoAnimFrame = null;
    }

    const canvas = document.getElementById('slot-holo-canvas');
    if (canvas) {
      canvas.classList.add('hidden');
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    const reelsFrame = document.getElementById('slot-reels-frame');
    if (reelsFrame) {
      reelsFrame.classList.remove('slot-emp-shake');
    }

    const reelsWindow = document.getElementById('slot-reels-window');
    if (reelsWindow) {
      reelsWindow.classList.remove('reels-dimmed');
    }

    // Restore Jumbotron Header
    const header = document.getElementById('slot-header-jumbotron') || document.querySelector('.slot-header');
    const badge = document.getElementById('slot-header-badge') || document.querySelector('.slot-badge');
    const title = document.getElementById('slot-header-title') || document.querySelector('.slot-title');
    if (header) {
      header.classList.remove('jumbotron-new', 'jumbotron-duplicate');
    }
    if (badge) badge.textContent = 'TEST YOUR LUCK';
    if (title) {
      title.textContent = 'SUPPLY SURGE';
      title.style.background = '';
      title.style.webkitBackgroundClip = '';
      title.style.webkitTextFillColor = '';
      title.style.filter = '';
    }

    // Fast skip via screen click/keypress during Auto Turbo: immediately trigger next spin!
    if (isManualScreenSkip && this._autoTurboActive && !this._autoTurboStopping) {
      if (wallet.getTickets() > 0) {
        this.executeSpin(true);
        return;
      } else {
        this._autoTurboActive = false;
        this._autoTurboStopping = false;
      }
    }

    this.updateTicketHeader();
  }

  _onDismissKey(e) {
    if (this._isOutcomeActive) {
      this.dismissOutcome(e);
    }
  }

  clearImageCache() {
    if (this._imgCache) {
      for (const img of this._imgCache.values()) {
        try {
          img.onload = null;
          img.onerror = null;
          img.src = '';
        } catch {}
      }
      this._imgCache.clear();
    }
  }

  _getCachedImage(url) {
    if (!url) return null;
    if (!this._imgCache.has(url)) {
      const img = new Image();
      img.src = url;
      this._imgCache.set(url, img);
    }
    return this._imgCache.get(url);
  }

  _drawLiveUnit(ctx, {
    unitType,
    race,
    unitName = '',
    cardData = null,
    unitImage = null,
    pedestalX = 0,
    pedestalY = 0,
    drawSize = 120,
    idleBob = 0,
    isFiring = false,
    cycleTime = 0,
    now = 0,
    elapsed = 0,
    recoilX = 0,
    targetX = null,
    targetY = null,
    isRocket = false,
    styleHex = null
  }) {
    const isLootingVulture = (unitName === 'Looting Vulture' || unitType === 'LootingVulture' || unitType === 'Looting_Vulture');

    if (isLootingVulture) {
      // 🐉 SUL LOOTING VULTURE CELESTIAL DRAGON TITAN
      // Fully articulated 4-segment celestial sky leviathan (Head, Body, Pyramid, Tail)
      const bodyBitmap = buildSpriteBitmap('LootingVulture_Body', race, 'western');
      const pyramidBitmap = buildSpriteBitmap('LootingVulture_Pyramid', race, 'western');
      const headBitmap = buildSpriteBitmap('LootingVulture_Head', race, 'western');
      const tailBitmap = buildSpriteBitmap('LootingVulture_Tail', race, 'western');

      const isFlash = isFiring && cycleTime < 45;
      const bodySrc = (isFlash && bodyBitmap?.flash) ? bodyBitmap.flash : bodyBitmap?.normal;
      const pyramidSrc = (isFlash && pyramidBitmap?.flash) ? pyramidBitmap.flash : pyramidBitmap?.normal;
      const headSrc = (isFlash && headBitmap?.flash) ? headBitmap.flash : headBitmap?.normal;
      const tailSrc = (isFlash && tailBitmap?.flash) ? tailBitmap.flash : tailBitmap?.normal;

      const renderX = pedestalX;
      const renderY = pedestalY - drawSize * 0.46;
      const forwardDir = 1; // Facing right toward target drone
      const recoilOffsetX = recoilX || 0;
      const t = now * 0.001;

      // 1. Celestial Leviathan Levitation Dynamics
      const hoverY = Math.sin(t * 1.3) * 5.0;
      const flightBank = Math.sin(t * 1.1) * 0.025;

      ctx.save();
      ctx.translate(renderX, renderY + hoverY);
      ctx.rotate(flightBank);
      ctx.translate(-renderX, -(renderY + hoverY));

      // ── TRAVELLING TRANSVERSE SINE WAVE KINEMATICS ──
      const omega = 2.3;
      const tailShiftX = -drawSize * 0.30 * forwardDir;
      const pyramidShiftX = drawSize * 0.18 * forwardDir;
      const headShiftX = drawSize * 0.22 * forwardDir;
      const pyramidScale = 0.78;

      const tailWavePhase = t * omega - 2.0;
      const tailSurgeX = Math.cos(tailWavePhase) * 4.5 * forwardDir;
      const tailWaveY = Math.sin(tailWavePhase) * 11.5 + Math.sin(tailWavePhase * 2.0 - 0.6) * 3.2;
      const tailPitch = (Math.cos(tailWavePhase) * 0.28 + Math.cos(tailWavePhase * 2.0) * 0.08) * forwardDir;

      const bodyWavePhase = t * omega - 0.4;
      const bodySurgeX = Math.cos(bodyWavePhase) * 2.8 * forwardDir;
      const bodyWaveY = Math.sin(bodyWavePhase) * 7.5 + Math.sin(bodyWavePhase * 2.0 - 0.4) * 2.2;
      const bodyPitch = (Math.cos(bodyWavePhase) * 0.16 + Math.cos(bodyWavePhase * 2.0) * 0.04) * forwardDir;

      const pyramidWavePhase = t * omega + 0.25;
      const pyramidSurgeX = Math.cos(pyramidWavePhase) * 1.8 * forwardDir;
      const pyramidWaveY = Math.sin(pyramidWavePhase) * 5.2 + Math.sin(pyramidWavePhase * 2.0 - 0.3) * 1.5;
      let pyramidPitch = (Math.cos(pyramidWavePhase) * 0.12 + Math.cos(pyramidWavePhase * 2.0) * 0.03) * forwardDir;

      const headLeadPhase = t * omega + 0.8;
      const headSurgeX = Math.cos(headLeadPhase) * 1.4 * forwardDir;
      const headBobY = Math.sin(headLeadPhase) * 4.6 + Math.sin(headLeadPhase * 2.0 - 0.2) * 1.2;
      let headPitch = (Math.cos(headLeadPhase) * 0.14 + Math.cos(headLeadPhase * 2.0) * 0.035) * forwardDir;

      // Dynamic aim tracking towards target reticle
      if (targetX !== null && targetY !== null) {
        const ddx = (targetX - renderX);
        const ddy = (targetY - (renderY + hoverY));
        if (ddx > 0) {
          const aimAngle = Math.atan2(ddy, ddx);
          const clampedAim = Math.max(-0.25, Math.min(0.25, aimAngle));
          headPitch += clampedAim;
          pyramidPitch += clampedAim * 0.45;
        }
      }

      // A. Trailing Caudal S-Curve Tail
      ctx.save();
      const tailPivotX = renderX + ((10 - 11.5) / 24) * drawSize * forwardDir + tailShiftX + tailSurgeX;
      const tailPivotY = (renderY + hoverY) + ((10 - 11.5) / 24) * drawSize;
      ctx.translate(tailPivotX, tailPivotY + tailWaveY);
      ctx.rotate(tailPitch);
      ctx.translate(-tailPivotX, -(tailPivotY + tailWaveY));

      if (tailSrc) {
        ctx.drawImage(
          tailSrc,
          renderX - drawSize / 2 + tailShiftX + tailSurgeX + recoilOffsetX,
          (renderY + hoverY + tailWaveY) - drawSize / 2,
          drawSize,
          drawSize
        );
      }

      // Ethereal Caudal Fin Ray Tendrils
      const finTipX = renderX + ((1 - 11.5) / 24) * drawSize * forwardDir + tailShiftX + tailSurgeX + recoilOffsetX;
      const finTipY = (renderY + hoverY + tailWaveY) + ((8 - 11.5) / 24) * drawSize;
      const finWhip = Math.sin(t * 3.4) * 5.0;
      ctx.strokeStyle = 'rgba(253, 224, 71, 0.85)';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(finTipX, finTipY);
      ctx.bezierCurveTo(
        finTipX - 12 * forwardDir, finTipY + finWhip * 0.4,
        finTipX - 22 * forwardDir, finTipY - finWhip * 0.6,
        finTipX - 34 * forwardDir, finTipY + finWhip
      );
      ctx.stroke();
      ctx.restore();

      // B. Serpentine Body Chassis
      ctx.save();
      ctx.translate(renderX + bodySurgeX + recoilOffsetX, renderY + hoverY + bodyWaveY);
      ctx.rotate(bodyPitch);
      ctx.translate(-(renderX + bodySurgeX + recoilOffsetX), -(renderY + hoverY + bodyWaveY));

      // Living Muscular Pelvis & Tail Bridge
      ctx.strokeStyle = '#1c1917';
      ctx.lineWidth = drawSize * 0.13;
      ctx.beginPath();
      const bodyPelvisX = renderX + ((5.5 - 11.5) / 24) * drawSize * forwardDir + bodySurgeX + recoilOffsetX;
      const bodyPelvisY = renderY + hoverY + bodyWaveY + ((11 - 11.5) / 24) * drawSize;
      ctx.moveTo(bodyPelvisX, bodyPelvisY);
      ctx.quadraticCurveTo(
        (bodyPelvisX + tailPivotX) / 2,
        Math.min(bodyPelvisY, tailPivotY + tailWaveY) - 3.0,
        tailPivotX,
        tailPivotY + tailWaveY
      );
      ctx.stroke();

      // Gold Sinew Thread on Pelvis Bridge
      ctx.strokeStyle = '#fde047';
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.moveTo(bodyPelvisX, bodyPelvisY - 0.5);
      ctx.quadraticCurveTo(
        (bodyPelvisX + tailPivotX) / 2,
        Math.min(bodyPelvisY, tailPivotY + tailWaveY) - 4.5,
        tailPivotX,
        tailPivotY + tailWaveY - 0.5
      );
      ctx.stroke();

      // Golden Vertebrae Spines along Pelvic Bridge
      for (let v = 1; v <= 3; v++) {
        const u = v / 4;
        const vx = bodyPelvisX + (tailPivotX - bodyPelvisX) * u;
        const vy = bodyPelvisY + (tailPivotY + tailWaveY - bodyPelvisY) * u - 3.0;
        ctx.fillStyle = '#fde047';
        ctx.fillRect(vx - 1.2, vy - 1.2, 2.4, 2.4);
      }

      if (bodySrc) {
        ctx.drawImage(
          bodySrc,
          renderX - drawSize / 2 + bodySurgeX + recoilOffsetX,
          (renderY + hoverY + bodyWaveY) - drawSize / 2,
          drawSize,
          drawSize
        );
      }

      // Coarse Fibrous Mane Tufts along Dorsal Crest
      for (let m = 0; m < 5; m++) {
        const mu = m / 4;
        const mgx = 6.5 + mu * 9.5;
        const mBaseX = renderX + ((mgx - 11.5) / 24) * drawSize * forwardDir + bodySurgeX + recoilOffsetX;
        const mRowY = 8.2 - Math.sin(mu * Math.PI) * 1.6;
        const mBaseY = renderY + hoverY + bodyWaveY + ((mRowY - 11.5) / 24) * drawSize;
        const mFlutter = Math.sin(t * 4.2 - m * 0.75) * 4.0;
        const mLen = 12 + (m % 2) * 6;
        const mTipX = mBaseX - (mLen * forwardDir);
        const mTipY = mBaseY - 8 + mFlutter;

        ctx.strokeStyle = '#1c1917';
        ctx.lineWidth = 2.4;
        ctx.beginPath();
        ctx.moveTo(mBaseX, mBaseY);
        ctx.quadraticCurveTo(mBaseX - (mLen * 0.45 * forwardDir), mBaseY - 6, mTipX, mTipY);
        ctx.stroke();

        ctx.strokeStyle = (m % 2 === 0 ? '#fde047' : '#ca8a04');
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(mBaseX + 0.5 * forwardDir, mBaseY);
        ctx.quadraticCurveTo(mBaseX - (mLen * 0.45 * forwardDir) + 0.5, mBaseY - 6.5, mTipX + 1.0 * forwardDir, mTipY - 0.5);
        ctx.stroke();
      }

      // Shaggy Fibrous Fur Fringes hanging from Belly Dip
      for (let b = 0; b < 4; b++) {
        const bu = b / 3;
        const bgx = 7.5 + bu * 6.5;
        const bBaseX = renderX + ((bgx - 11.5) / 24) * drawSize * forwardDir + bodySurgeX + recoilOffsetX;
        const bRowY = 14.5 + Math.sin(bu * Math.PI) * 1.5;
        const bBaseY = renderY + hoverY + bodyWaveY + ((bRowY - 11.5) / 24) * drawSize;
        const bSway = Math.sin(t * 3.6 - b * 0.9) * 3.6;
        const bLen = 11 + (b % 2) * 6;
        const bTipX = bBaseX - (bLen * 1.2 * forwardDir);
        const bTipY = bBaseY + 8 + bSway;

        ctx.strokeStyle = '#292524';
        ctx.lineWidth = 2.0;
        ctx.beginPath();
        ctx.moveTo(bBaseX, bBaseY);
        ctx.quadraticCurveTo(bBaseX - (bLen * 0.5 * forwardDir), bBaseY + 4, bTipX, bTipY);
        ctx.stroke();

        ctx.strokeStyle = '#eab308';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(bBaseX, bBaseY);
        ctx.quadraticCurveTo(bBaseX - (bLen * 0.5 * forwardDir), bBaseY + 4.5, bTipX, bTipY);
        ctx.stroke();
      }

      // Golden Sand Cascade from belly
      const dustPhase = (t * 1.8) % 1.0;
      for (let d = 0; d < 5; d++) {
        const dOffset = (dustPhase + d * 0.20) % 1.0;
        const dSpanX = renderX - ((6 + dOffset * 24) * forwardDir) + bodySurgeX + recoilOffsetX;
        const dSpanY = (renderY + hoverY + bodyWaveY) + 18 + (dOffset * 28);
        const dAlpha = (1.0 - dOffset) * 0.85;
        ctx.fillStyle = `rgba(254, 240, 138, ${dAlpha})`;
        ctx.fillRect(dSpanX - 1.0, dSpanY - 1.0, 2.0, 2.0);
      }
      ctx.restore();

      // C. Living Muscular Ventral Throat Ridge
      const bodyChestX = renderX + ((16.5 - 11.5) / 24) * drawSize * forwardDir + bodySurgeX + recoilOffsetX;
      const bodyChestY = renderY + hoverY + bodyWaveY + ((11.5 - 11.5) / 24) * drawSize;
      const headThroatX = renderX + ((14.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const headThroatY = (renderY + hoverY + bodyWaveY + headBobY) + ((12 - 11.5) / 24) * drawSize;

      ctx.strokeStyle = '#1c1917';
      ctx.lineWidth = drawSize * 0.08;
      ctx.beginPath();
      ctx.moveTo(bodyChestX, bodyChestY);
      ctx.quadraticCurveTo(
        (bodyChestX + headThroatX) / 2,
        Math.max(bodyChestY, headThroatY) + 1.5,
        headThroatX,
        headThroatY
      );
      ctx.stroke();

      ctx.strokeStyle = '#fde047';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(bodyChestX, bodyChestY + 1.0);
      ctx.quadraticCurveTo(
        (bodyChestX + headThroatX) / 2,
        Math.max(bodyChestY, headThroatY) + 2.5,
        headThroatX,
        headThroatY + 1.0
      );
      ctx.stroke();

      // D. Fused 3D Megalithic Pyramid
      const pyramidPivotX = renderX + ((13.5 - 11.5) / 24) * drawSize * forwardDir + pyramidShiftX + pyramidSurgeX + recoilOffsetX;
      const pyramidPivotY = (renderY + hoverY + pyramidWaveY) + ((9 - 11.5) / 24) * drawSize;

      ctx.save();
      ctx.translate(pyramidPivotX, pyramidPivotY);
      ctx.rotate(pyramidPitch);
      ctx.scale(pyramidScale, pyramidScale);
      ctx.translate(-pyramidPivotX, -pyramidPivotY);

      if (pyramidSrc) {
        ctx.drawImage(
          pyramidSrc,
          renderX - drawSize / 2 + pyramidShiftX + pyramidSurgeX + recoilOffsetX,
          (renderY + hoverY + pyramidWaveY) - drawSize / 2,
          drawSize,
          drawSize
        );
      }
      ctx.restore();

      // E. Autonomous Articulated Dragon Skull
      const neckJointX = renderX + ((14 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const neckJointY = (renderY + hoverY + bodyWaveY) + ((10 - 11.5) / 24) * drawSize;

      ctx.save();
      ctx.translate(neckJointX, neckJointY + headBobY);
      ctx.rotate(headPitch);
      ctx.translate(-neckJointX, -(neckJointY + headBobY));

      if (headSrc) {
        ctx.drawImage(
          headSrc,
          renderX - drawSize / 2 + headShiftX + headSurgeX + recoilOffsetX,
          (renderY + hoverY + bodyWaveY + headBobY) - drawSize / 2,
          drawSize,
          drawSize
        );
      }

      // Flowing Celestial Whisker Barbels
      const chinX = renderX + ((18.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const chinY = (renderY + hoverY + bodyWaveY + headBobY) + ((13.5 - 11.5) / 24) * drawSize;

      // Barbel 1
      const b1WaveA = Math.sin(t * 3.6) * 3.0;
      const b1WaveB = Math.cos(t * 7.2) * 1.8;
      const b1TipX = chinX - (22 * forwardDir);
      const b1TipY = chinY + 12 + b1WaveA + b1WaveB;
      const b1Ctrl1X = chinX - (6 * forwardDir);
      const b1Ctrl1Y = chinY + 13 + b1WaveA * 0.5;
      const b1Ctrl2X = chinX - (15 * forwardDir);
      const b1Ctrl2Y = chinY + 7 - b1WaveA * 0.4;

      ctx.strokeStyle = '#fde047';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(chinX, chinY);
      ctx.bezierCurveTo(b1Ctrl1X, b1Ctrl1Y, b1Ctrl2X, b1Ctrl2Y, b1TipX, b1TipY);
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(b1TipX, b1TipY, 1.2, 0, Math.PI * 2);
      ctx.fill();

      // Barbel 2
      const b2WaveA = Math.sin(t * 4.2 + 1.2) * 2.5;
      const b2TipX = chinX - (16 * forwardDir);
      const b2TipY = chinY + 16 + b2WaveA;
      const b2Ctrl1X = chinX - (4 * forwardDir);
      const b2Ctrl1Y = chinY + 9;
      const b2Ctrl2X = chinX - (10 * forwardDir);
      const b2Ctrl2Y = chinY + 14 + b2WaveA * 0.6;

      ctx.strokeStyle = '#eab308';
      ctx.lineWidth = 1.1;
      ctx.beginPath();
      ctx.moveTo(chinX - (1 * forwardDir), chinY + 1);
      ctx.bezierCurveTo(b2Ctrl1X, b2Ctrl1Y, b2Ctrl2X, b2Ctrl2Y, b2TipX, b2TipY);
      ctx.stroke();

      // Horn Tip Specular Gleam
      const hornTipX = renderX + ((23.0 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const hornTipY = (renderY + hoverY + bodyWaveY + headBobY) + ((9.0 - 11.5) / 24) * drawSize;
      const hornGlint = 0.5 + 0.5 * Math.sin(t * 3.5 + 0.8);
      ctx.fillStyle = `rgba(255, 255, 255, ${0.4 + 0.5 * hornGlint})`;
      ctx.beginPath();
      ctx.arc(hornTipX, hornTipY, 1.2 * (0.8 + 0.4 * hornGlint), 0, Math.PI * 2);
      ctx.fill();

      // Glowing Amber Compound Eyes Glints
      const eyeShimmer = 0.6 + 0.4 * Math.sin(t * 4.2 + 1.2);
      const eye1X = renderX + ((16.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const eye1Y = (renderY + hoverY + bodyWaveY + headBobY) + ((6.8 - 11.5) / 24) * drawSize;
      const eye2X = renderX + ((18.2 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const eye2Y = (renderY + hoverY + bodyWaveY + headBobY) + ((7.8 - 11.5) / 24) * drawSize;
      ctx.fillStyle = `rgba(255, 255, 255, ${0.75 * eyeShimmer})`;
      ctx.beginPath();
      ctx.arc(eye1X, eye1Y, 0.9, 0, Math.PI * 2);
      ctx.arc(eye2X, eye2Y, 0.9, 0, Math.PI * 2);
      ctx.fill();

      // Golden sand cascade pouring downwards from maw
      const mawX = renderX + ((19.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX + recoilOffsetX;
      const mawY = (renderY + hoverY + bodyWaveY + headBobY) + ((12.0 - 11.5) / 24) * drawSize;
      const sandPal = ['#fef08a', '#fde047', '#facc15', '#ca8a04', '#ffffff'];
      for (let sIdx = 0; sIdx < 5; sIdx++) {
        const sPhase = (t * 2.6 + sIdx * 0.2) % 1.0;
        const sAlpha = Math.sin(sPhase * Math.PI) * 0.85;
        const dropDist = sPhase * 20.0;
        const driftBack = (sPhase * sPhase * 12.0 + Math.sin(sPhase * 6.0 + sIdx) * 2.0) * forwardDir;
        const sx = mawX - driftBack;
        const sy = mawY + dropDist;
        const sRadius = sIdx === 4 ? 0.9 : (sIdx % 2 === 0 ? 1.2 : 1.0);
        ctx.fillStyle = sandPal[sIdx % sandPal.length];
        ctx.globalAlpha = Math.max(0, sAlpha);
        ctx.beginPath();
        ctx.arc(sx, sy, sRadius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1.0;

      ctx.restore();

      // F. Radiant Solar Apex Star on Pyramid Capstone
      const scaledApexDistY = ((0.5 - 9) / 24) * drawSize * pyramidScale;
      const cosP = Math.cos(pyramidPitch);
      const sinP = Math.sin(pyramidPitch);
      const apexPx = pyramidPivotX - scaledApexDistY * sinP;
      const apexPy = pyramidPivotY + scaledApexDistY * cosP;

      const isAttackingFlare = isFiring;
      const flarePulse = (isAttackingFlare ? 1.8 : (0.75 + 0.25 * Math.sin(t * 4.5)));
      const flareR = Math.max(0.1, drawSize * 0.12 * flarePulse * pyramidScale);

      const flareGrad = ctx.createRadialGradient(apexPx, apexPy, 1, apexPx, apexPy, Math.max(1.1, flareR));
      flareGrad.addColorStop(0, 'rgba(255, 255, 255, 0.98)');
      flareGrad.addColorStop(0.3, 'rgba(254, 240, 138, 0.80)');
      flareGrad.addColorStop(0.65, 'rgba(234, 179, 8, 0.35)');
      flareGrad.addColorStop(1, 'rgba(217, 119, 6, 0)');
      ctx.fillStyle = flareGrad;
      ctx.beginPath();
      ctx.arc(apexPx, apexPy, flareR, 0, Math.PI * 2);
      ctx.fill();

      // 4-Point Anamorphic Star Cross
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(apexPx - flareR * 1.2, apexPy);
      ctx.lineTo(apexPx + flareR * 1.2, apexPy);
      ctx.moveTo(apexPx, apexPy - flareR * 1.2);
      ctx.lineTo(apexPx, apexPy + flareR * 1.2);
      ctx.stroke();

      ctx.restore(); // restores flightBank & hoverY transform

      return {
        muzzleX: apexPx,
        muzzleY: apexPy,
        isArticulated: true
      };
    } else if (unitName === 'Delving Pillar' || unitType === 'DelvingPillar' || unitType === 'Delving_Pillar') {
      // 𓉐 SUL DELVING PILLAR: 9 Living Matrix Tentacles across 3 Depth Planes + Compound Oculars
      const pillarSize = drawSize * 0.76;
      const t = now * 0.001;
      const hoverPhase = t * 0.75;
      const hoverBob = Math.sin(hoverPhase) * 3.6 + Math.sin(hoverPhase * 0.5 + 1.1) * 1.2;
      const propCycle = (t * 1.4) % (Math.PI * 2);
      const strokeWhip = Math.sin(propCycle);
      const strokeSpread = Math.cos(propCycle);
      const breathPulse = Math.sin(t * 1.2);
      const stretchY = 1.0 + breathPulse * 0.018;
      const squashX = 1.0 - breathPulse * 0.012;
      const idleTilt = Math.sin(t * 0.5) * 0.025;
      const recoilOffsetX = recoilX || 0;
      const pillarX = pedestalX + recoilOffsetX;
      const pillarY = pedestalY - drawSize * 0.50 + hoverBob;
      const cosT = Math.cos(idleTilt);
      const sinT = Math.sin(idleTilt);

      // 1. Calculate All 9 Tentacle Paths
      for (let s = 0; s < 9; s++) {
        const sFreq = DELVING_TENTACLE_FREQ[s];
        const sPhase = DELVING_TENTACLE_PHASE[s];
        const sSpatial = DELVING_TENTACLE_SPATIAL[s];
        const sAmp = DELVING_TENTACLE_AMP[s];
        const sCurlFreq = DELVING_TENTACLE_CURL_FREQ[s];
        const sCurlAmp = DELVING_TENTACLE_CURL_AMP[s];

        const localAnchorX = DELVING_TENTACLE_ANCHOR_X[s] * pillarSize * squashX;
        const localAnchorY = DELVING_TENTACLE_ANCHOR_Y[s] * pillarSize * stretchY;
        const anchorX = pillarX + cosT * localAnchorX - sinT * localAnchorY;
        const anchorY = pillarY + sinT * localAnchorX + cosT * localAnchorY;
        const strandLen = pillarSize * DELVING_TENTACLE_LEN[s] * stretchY;

        _slotDelvingTentaclesScratch[s][0].x = anchorX;
        _slotDelvingTentaclesScratch[s][0].y = anchorY;

        for (let k = 1; k <= 9; k++) {
          const u = k / 9;
          const wavePhase1 = t * (2.4 * sFreq) - u * sSpatial + sPhase;
          const wavePhase2 = t * (1.2 * sFreq) - u * 3.4 + sPhase;
          const idleWaveX = Math.sin(wavePhase1) * (u * 5.0 * sAmp) + Math.cos(wavePhase2) * (u * 2.0);
          const idleWaveY = Math.cos(wavePhase1 * 0.8) * (u * 2.5 * sAmp);

          const sweepX = -Math.max(0, strokeWhip) * (u * pillarSize * 0.15);
          const sweepY = strokeSpread * (u * pillarSize * 0.08);

          let curlX = 0;
          let curlY = 0;
          if (k >= 5) {
            const curlWeight = (k - 4) / 5;
            curlX = Math.sin(t * sCurlFreq + sPhase * 1.6) * (curlWeight * sCurlAmp);
            curlY = Math.cos(t * (sCurlFreq * 0.85) + sPhase * 1.6) * (curlWeight * sCurlAmp * 0.6);
          }

          const lateralSpread = (s - 4) * (0.06 * pillarSize * u);

          _slotDelvingTentaclesScratch[s][k].x = anchorX + idleWaveX + sweepX + curlX + lateralSpread;
          _slotDelvingTentaclesScratch[s][k].y = anchorY + (u * strandLen) + idleWaveY + sweepY + curlY;
        }
      }

      // 2. LAYER A: Background Tentacles (0..2)
      ctx.save();
      ctx.lineCap = 'round';
      for (let k = 1; k <= 9; k++) {
        const prevIdx = k - 1;
        ctx.strokeStyle = DELVING_TENTACLE_BG_COLORS[k - 1];
        ctx.lineWidth = Math.max(1.0, DELVING_TENTACLE_SEG_WIDTHS[k - 1] * 0.9);
        ctx.beginPath();
        for (let s = 0; s < 3; s++) {
          ctx.moveTo(_slotDelvingTentaclesScratch[s][prevIdx].x, _slotDelvingTentaclesScratch[s][prevIdx].y);
          ctx.lineTo(_slotDelvingTentaclesScratch[s][k].x, _slotDelvingTentaclesScratch[s][k].y);
        }
        ctx.stroke();
      }
      ctx.restore();

      // 3. LAYER B: Body Sprite & Compound Sensory Ocular Cluster
      const isFlash = isFiring && cycleTime < 45;
      const spriteKey = isFiring ? 'DelvingPillar_Attack' : 'DelvingPillar_Hover';
      const pillarBitmap = buildSpriteBitmap(spriteKey, race, 'western') || buildSpriteBitmap('DelvingPillar', race, 'western');
      const activeImg = (isFlash && pillarBitmap?.flash) ? pillarBitmap.flash : pillarBitmap?.normal;
      ctx.save();
      ctx.translate(pillarX, pillarY);
      ctx.rotate(idleTilt);
      ctx.scale(squashX, stretchY);
      if (activeImg) {
        ctx.drawImage(activeImg, -pillarSize / 2, -pillarSize / 2, pillarSize, pillarSize);
      }
      const snoutOffX = pillarSize * 0.22;
      const snoutOffY = -pillarSize * 0.02;
      const oR = pillarSize * 0.24;
      const eyeGrad = ctx.createRadialGradient(snoutOffX, snoutOffY, 1, snoutOffX, snoutOffY, oR);
      eyeGrad.addColorStop(0, 'rgba(254, 240, 138, 0.9)');
      eyeGrad.addColorStop(0.5, 'rgba(250, 204, 21, 0.4)');
      eyeGrad.addColorStop(1, 'rgba(234, 179, 8, 0)');
      ctx.fillStyle = eyeGrad;
      ctx.beginPath();
      ctx.arc(snoutOffX, snoutOffY, oR, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // 4. LAYER C: Foreground Tentacles (3..8)
      ctx.save();
      ctx.lineCap = 'round';
      for (let k = 1; k <= 9; k++) {
        const prevIdx = k - 1;
        ctx.strokeStyle = DELVING_TENTACLE_FG_COLORS[k - 1];
        ctx.lineWidth = Math.max(1.0, DELVING_TENTACLE_SEG_WIDTHS[k - 1] * 1.0);
        ctx.beginPath();
        for (let s = 3; s < 9; s++) {
          ctx.moveTo(_slotDelvingTentaclesScratch[s][prevIdx].x, _slotDelvingTentaclesScratch[s][prevIdx].y);
          ctx.lineTo(_slotDelvingTentaclesScratch[s][k].x, _slotDelvingTentaclesScratch[s][k].y);
        }
        ctx.stroke();
      }

      // Luminous Searchlight Bulbs & Halos
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      for (let s = 0; s < 9; s++) {
        const tip = _slotDelvingTentaclesScratch[s][9];
        const r = (s === 4 || s === 7) ? 2.2 : 1.6;
        ctx.moveTo(tip.x + r, tip.y);
        ctx.arc(tip.x, tip.y, r, 0, Math.PI * 2);
      }
      ctx.fill();

      for (let s = 1; s < 9; s += 2) {
        const tip = _slotDelvingTentaclesScratch[s][9];
        const tipGrad = ctx.createRadialGradient(tip.x, tip.y, 1, tip.x, tip.y, 8);
        tipGrad.addColorStop(0, 'rgba(254, 240, 138, 0.8)');
        tipGrad.addColorStop(0.5, 'rgba(234, 179, 8, 0.3)');
        tipGrad.addColorStop(1, 'rgba(234, 179, 8, 0)');
        ctx.fillStyle = tipGrad;
        ctx.beginPath();
        ctx.arc(tip.x, tip.y, 8, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      return {
        muzzleX: pillarX + pillarSize * 0.45,
        muzzleY: pillarY,
        isArticulated: true
      };

    } else if (unitName === 'Processing Pillar' || unitType === 'ProcessingPillar' || unitType === 'Processing_Pillar') {
      // 𓉐 SUL PROCESSING PILLAR 36x36 HD NATIVE DUAL-LAYER RIG (Supply Surge)
      const pillarSize = drawSize * 0.912; // 4% kleiner (0.95 * 0.96 = 0.912)
      const t = now * 0.001;
      const hoverWave = Math.sin(t * 1.6) * 3.6 + Math.sin(t * 0.8) * 1.2;
      const breathPulse = Math.sin(t * 1.0);
      const stretchY = 1.0 + breathPulse * 0.022;
      const squashX = 1.0 - breathPulse * 0.016;
      const recoilOffsetX = recoilX || 0;
      const pillarX = pedestalX + recoilOffsetX;
      const pillarY = pedestalY - drawSize * 0.46 + hoverWave;

      // Contact shadow
      ctx.save();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.28)';
      ctx.beginPath();
      ctx.ellipse(pedestalX + recoilOffsetX * 0.5, pedestalY, 60 * squashX, 18 * stretchY, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Render Authentic 36x36 HD Dual-Layer Rig
      drawProcessingPillarRig(ctx, {
        x: pillarX,
        y: pillarY,
        size: pillarSize,
        dir: 1,
        team: 'western',
        isHitFlashing: (isFiring && cycleTime < 45),
        isCharging: false,
        isAttack: isFiring,
        isAir: false,
        chargeProgress: isFiring ? 1.0 : 0,
        time: t,
        wobble: 0,
        quality: 'high',
        bulletColorHex: '#38bdf8'
      });

      return {
        muzzleX: pillarX + (26 * (pillarSize / 36.0)),
        muzzleY: pillarY - (6 * (pillarSize / 36.0)),
        isArticulated: true
      };

    } else if (unitName === 'Sentinal Lock' || unitType === 'SentinalLock' || unitType === 'Sentinal_Lock') {
      // 👁️ SUL SENTINAL LOCK: Organic Sentinel with Head Eye Glow & 3 Tail Tentacle Stubs
      const t = now * 0.001;
      const recoilOffsetX = recoilX || 0;
      const drawX = Math.round(pedestalX - drawSize * 0.5 + recoilOffsetX);
      const drawY = Math.round(pedestalY - drawSize + 16 + (idleBob || 0));

      const isFlash = isFiring && cycleTime < 45;
      const spriteBmp = buildSpriteBitmap('SentinalLock_Body', race, 'western') || buildSpriteBitmap(unitType, race, 'western');
      const bmpToDraw = (isFlash && spriteBmp?.flash) ? spriteBmp.flash : spriteBmp?.normal;
      if (bmpToDraw) {
        ctx.drawImage(bmpToDraw, drawX, drawY, drawSize, drawSize);
      }

      // Head Eye Glow
      const headEyeX = drawX + drawSize * 0.72;
      const headEyeY = drawY + drawSize * 0.38;
      const eyePulse = 0.8 + 0.2 * Math.sin(t * 4.0);
      ctx.save();
      const eyeGrad = ctx.createRadialGradient(headEyeX, headEyeY, 1, headEyeX, headEyeY, 10);
      eyeGrad.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
      eyeGrad.addColorStop(0.4, 'rgba(234, 179, 8, 0.75)');
      eyeGrad.addColorStop(1, 'rgba(234, 179, 8, 0)');
      ctx.fillStyle = eyeGrad;
      ctx.beginPath();
      ctx.arc(headEyeX, headEyeY, 10 * eyePulse, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // 3 Vestigial Tail Tentacle Stubs
      ctx.save();
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#ca8a04';
      ctx.lineWidth = 1.8;
      const tailAttachX = drawX + drawSize * 0.16;
      const tailAttachY = drawY + drawSize * 0.70;
      for (let c = 0; c < 3; c++) {
        const stubOffX = (c - 1) * (drawSize * 0.045);
        const stubPhase = t * 5.5 + c * 2.1;
        const stubLen = drawSize * 0.06;
        const stubSwing = Math.sin(stubPhase) * (drawSize * 0.08);
        ctx.beginPath();
        ctx.moveTo(tailAttachX + stubOffX, tailAttachY);
        ctx.lineTo(tailAttachX + stubOffX - stubSwing, tailAttachY + stubLen);
        ctx.stroke();
      }
      ctx.restore();

      return {
        muzzleX: drawX + drawSize * 0.82,
        muzzleY: drawY + drawSize * 0.44,
        isArticulated: true
      };

    } else if (unitName === 'Pink Beret' || unitType === 'PinkBeret' || unitType === 'Pink_Beret') {
      // 🎖️ PINK BERET (48×48 UHD Native Relaunch) in Supply Surge Holo-Stage
      const hasGold = inventory.hasSpecialCard(unitName, 'gold');
      const isGold = (inventory.isSpecialCardActive ? inventory.isSpecialCardActive(unitName, 'gold') : hasGold)
        || cardData?.spriteRecolor === 'gold' || cardData?.edition === 'gold';
      const isClassic = !!(cardData?.variant === 'classic' || cardData?.forceHD);
      const prefix = isClassic
        ? (isGold ? 'PinkBeretGold_Classic_' : 'PinkBeret_Classic_')
        : (isGold ? 'PinkBeretGold_UHD_' : 'PinkBeret_UHD_');

      const pbDrawSize = Math.round(drawSize * (isClassic ? 1.12 : 1.20));
      const drawX = Math.round(pedestalX - pbDrawSize * 0.5 + (recoilX || 0));
      const drawY = Math.round(pedestalY - pbDrawSize + 16 + (idleBob || 0));

      if (isFiring) {
        const isFlash = cycleTime < 45;
        const gunKey = isRocket ? (prefix + 'Launcher_Fire') : (prefix + 'Gunfire');
        const gunBmp = buildSpriteBitmap(gunKey, race, 'western');
        const bmpToDraw = (isFlash && gunBmp?.flash) ? gunBmp.flash : gunBmp?.normal;
        if (bmpToDraw) {
          ctx.drawImage(bmpToDraw, drawX, drawY, pbDrawSize, pbDrawSize);
        }
        return {
          muzzleX: isRocket ? Math.round(drawX + pbDrawSize * 0.82) : Math.round(drawX + pbDrawSize * 0.88),
          muzzleY: isRocket ? Math.round(drawY + pbDrawSize * 0.32) : Math.round(drawY + pbDrawSize * 0.46),
          isArticulated: true
        };
      } else {
        // Continuous Catwalk Armed Walk Cycle (32 frames at 16 fps, 2.0s period)
        const walkPhase32 = ((now * 0.0005) % 1.0 + 1.0) % 1.0;
        const phaseIdx = Math.floor(walkPhase32 * 32);
        const frameNum = (phaseIdx % 32) + 1;
        const walkKey = prefix + 'Walk_Armed' + frameNum;
        const walkBmp = buildSpriteBitmap(walkKey, race, 'western');
        if (walkBmp?.normal) {
          ctx.drawImage(walkBmp.normal, drawX, drawY, pbDrawSize, pbDrawSize);
        }
        return {
          muzzleX: Math.round(drawX + pbDrawSize * 0.88),
          muzzleY: Math.round(drawY + pbDrawSize * 0.46),
          isArticulated: true
        };
      }

    } else if (unitName === 'Preserver Lock' || unitType === 'PreserverLock' || unitType === 'Preserver_Lock') {
      // 🕷️ SUL PRESERVER LOCK: Towering Hexapod with Corona & Dual Projector Mast
      const t = now * 0.001;
      const recoilOffsetX = recoilX || 0;
      const drawX = Math.round(pedestalX - drawSize * 0.5 + recoilOffsetX);
      const drawY = Math.round(pedestalY - drawSize + 16 + (idleBob || 0));

      const phaseIdx = Math.floor((t * 4) % 4);
      const legsKey = PRESERVER_LOCK_WALK_FRAMES[phaseIdx] || 'PreserverLock_Legs_Walk1';
      const bodyKey = PRESERVER_LOCK_BODY_FRAMES[phaseIdx] || 'PreserverLock_Body_Walk1';
      const walkBob = (phaseIdx === 0 || phaseIdx === 2) ? 2.0 : 0;
      const bodyRoll = (phaseIdx === 0) ? 0.04 : ((phaseIdx === 2) ? -0.04 : 0.0);

      const isFlash = isFiring && cycleTime < 45;
      const legsBmp = buildSpriteBitmap(legsKey, race, 'western');
      const bodyBmp = buildSpriteBitmap(bodyKey, race, 'western');

      const legsSrc = (isFlash && legsBmp?.flash) ? legsBmp.flash : legsBmp?.normal;
      const bodySrc = (isFlash && bodyBmp?.flash) ? bodyBmp.flash : bodyBmp?.normal;

      if (legsSrc) ctx.drawImage(legsSrc, drawX, drawY, drawSize, drawSize);
      if (bodySrc) {
        ctx.save();
        const pivotX = drawX + drawSize / 2;
        const pivotY = drawY + drawSize * 0.65;
        ctx.translate(pivotX, pivotY + walkBob);
        ctx.rotate(bodyRoll);
        ctx.drawImage(bodySrc, -drawSize / 2, -drawSize * 0.65, drawSize, drawSize);
        ctx.restore();
      }

      // Head projector corona / lens flare at twin lenses at mast peak
      const headX = drawX + drawSize * 0.51;
      const headY = drawY + drawSize * (1.5 / 24) + walkBob;
      const flarePulse = isFiring
        ? (0.95 + 0.15 * Math.sin(t * 20))
        : (0.70 + 0.15 * Math.sin(t * 4));
      const flareR = drawSize * (isFiring ? 0.20 : 0.12) * flarePulse;

      const coronaCanvas = getPreserverLockCoronaCanvas(isFiring, styleHex);
      if (coronaCanvas) {
        ctx.save();
        ctx.globalAlpha = isFiring ? 0.95 : 0.70;
        ctx.drawImage(coronaCanvas, headX - flareR, headY - flareR, flareR * 2, flareR * 2);
        ctx.restore();
      }

      if (isFiring) {
        ctx.save();
        ctx.strokeStyle = styleHex || 'rgba(224, 242, 254, 0.85)';
        ctx.lineWidth = 1.4;
        const rayLen = flareR * 1.5;
        ctx.beginPath();
        ctx.moveTo(headX - rayLen, headY);
        ctx.lineTo(headX + rayLen, headY);
        ctx.moveTo(headX, headY - rayLen);
        ctx.lineTo(headX + rayLen, headY);
        ctx.stroke();
        ctx.restore();
      }

      return {
        muzzleX: headX,
        muzzleY: headY,
        isArticulated: true
      };

    } else {
      // Standard Unit rendering
      const spriteBmp = buildSpriteBitmap(unitType, race, 'western');
      const isFlash = isFiring && cycleTime < 45;
      const bmpToDraw = (isFlash && spriteBmp?.flash) ? spriteBmp.flash : spriteBmp?.normal;

      const drawX = Math.round(pedestalX - drawSize * 0.5 + (recoilX || 0));
      const drawY = Math.round(pedestalY - drawSize + 16 + (idleBob || 0));

      if (bmpToDraw) {
        ctx.drawImage(bmpToDraw, drawX, drawY, drawSize, drawSize);
      } else {
        const thumbUrl = getCardThumbUrl(cardData?.images?.[0] || unitImage, 's');
        const img = this._getCachedImage(thumbUrl);
        if (img && img.complete) {
          ctx.save();
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(drawX, drawY, drawSize * 0.85, drawSize, 8);
          else ctx.rect(drawX, drawY, drawSize * 0.85, drawSize);
          ctx.clip();
          ctx.drawImage(img, drawX, drawY, drawSize * 0.85, drawSize);
          ctx.restore();
        }
      }

      return {
        muzzleX: drawX + drawSize * 0.84,
        muzzleY: drawY + drawSize * 0.50,
        isArticulated: false
      };
    }
  }

  _startHoloStage(result) {
    const canvas = document.getElementById('slot-holo-canvas');
    if (!canvas) return;
    canvas.classList.remove('hidden');

    const reelsWindow = document.getElementById('slot-reels-window');
    const rwRect = reelsWindow?.getBoundingClientRect();
    const rect = canvas.getBoundingClientRect();
    const isMobile = typeof window !== 'undefined' && window.matchMedia && (window.matchMedia('(max-width: 900px)').matches || window.matchMedia('(pointer: coarse)').matches);
    const dpr = isMobile ? 1.0 : Math.min(typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1, 2);
    const cssW = (rwRect && rwRect.width > 0) ? rwRect.width : (rect.width > 0 ? rect.width : 580);
    const cssH = (rwRect && rwRect.height > 0) ? rwRect.height : (rect.height > 0 ? rect.height : 420);
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Thermal Optimization: Zero-cost shadow glow bypass
    // Setting shadowBlur triggers expensive multi-pass Gaussian blur rasterization in GPU/compositor.
    try {
      Object.defineProperty(ctx, 'shadowBlur', {
        get: () => 0,
        set: () => {},
        configurable: true
      });
    } catch (_) {}

    if (this._holoAnimFrame) {
      cancelAnimationFrame(this._holoAnimFrame);
      this._holoAnimFrame = null;
    }

    let startTime = null;
    let lastTime = null;

    // Unit data resolution
    const unitName = result.unit?.name || '';
    const cardData = unitName ? CARDS_DATA.find(c => c.name === unitName) : null;
    const unitType = cardData?.unitType || unitName.replace(/\s+/g, '');
    const race = cardData?.race || 'Human';
    const isLootingVulture = (unitName === 'Looting Vulture' || unitType === 'LootingVulture' || unitType === 'Looting_Vulture');

    const empColor = result.isDuplicate ? '#ffcc00' : (result.style?.hex || '#00f0ff');

    // Cyber-EMP Spark Detonation System: 28 high-velocity motes & sparks (14 on mobile to reduce fillrate)
    const empParticles = [];
    const empCount = isMobile ? 14 : 28;
    const empCx = cssW * 0.5;
    const empCy = cssH * 0.5;
    for (let i = 0; i < empCount; i++) {
      const angle = (Math.PI * 2 * i) / empCount + (Math.random() - 0.5) * 0.35;
      const speed = 280 + Math.random() * 420;
      empParticles.push({
        x: empCx,
        y: empCy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed * 0.68,
        len: 10 + Math.random() * 18,
        size: 2.2 + Math.random() * 2.5,
        color: (i % 3 === 0) ? '#ffffff' : ((i % 3 === 1) ? empColor : `${empColor}dd`),
        drag: 0.88 + Math.random() * 0.08
      });
    }

    // Duplicate particle system & responsive badge configuration
    const particles = [];
    let badgeW = 320;
    let badgeH = 74;
    let refundFontSize = 21;
    let subFontSize = 13;
    const refundAmount = result.refundAmount || 100;
    const refundText = `+${refundAmount} FUGAZI REFUND`;
    const subText = '⬡ DUPLICATE DETECTED';

    if (result.isDuplicate) {
      // Responsive badge width: safely fill reels window while preserving margins on mobile
      badgeW = Math.min(Math.max(240, cssW - 24), 340);
      const maxTextW = badgeW - 32;

      ctx.font = `900 ${refundFontSize}px "Orbitron", sans-serif`;
      while (ctx.measureText(refundText).width > maxTextW && refundFontSize > 13) {
        refundFontSize -= 1;
        ctx.font = `900 ${refundFontSize}px "Orbitron", sans-serif`;
      }

      ctx.font = `900 ${subFontSize}px "Share Tech Mono", monospace`;
      while (ctx.measureText(subText).width > maxTextW && subFontSize > 9) {
        subFontSize -= 1;
        ctx.font = `900 ${subFontSize}px "Share Tech Mono", monospace`;
      }

      const count = isMobile ? 10 : 18;
      const cx = cssW * 0.5;
      const cy = cssH * 0.5;
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
        const speed = 130 + Math.random() * 220;
        particles.push({
          x: cx + (Math.random() - 0.5) * 40,
          y: cy + (Math.random() - 0.5) * 40,
          vx: Math.cos(angle) * speed + 55,
          vy: Math.sin(angle) * speed - 170,
          rot: Math.random() * Math.PI * 2,
          vRot: (Math.random() - 0.5) * 7,
          size: 14 + Math.random() * 12,
          color: Math.random() < 0.65 ? '#ffcc00' : '#f97316'
        });
      }
    }

    const renderLoop = (now) => {
      if (!this._isOutcomeActive || !this.isOpen()) {
        if (this._holoAnimFrame) {
          cancelAnimationFrame(this._holoAnimFrame);
          this._holoAnimFrame = null;
        }
        return;
      }
      if (typeof document !== 'undefined' && document.hidden) {
        if (this._holoAnimFrame) {
          cancelAnimationFrame(this._holoAnimFrame);
          this._holoAnimFrame = null;
        }
        const onVisible = () => {
          if (!document.hidden) {
            document.removeEventListener('visibilitychange', onVisible);
            if (this._isOutcomeActive && this.isOpen()) {
              this._holoAnimFrame = requestAnimationFrame(renderLoop);
            }
          }
        };
        document.addEventListener('visibilitychange', onVisible);
        return;
      }
      if (startTime === null) {
        startTime = now;
        lastTime = now;
      }
      // High-refresh throttle: On mobile devices throttle to ~35 FPS to halve fillrate/thermal load; on desktop cap to ~60 FPS (15ms)
      const minIntervalMs = isMobile ? 28.0 : 15.0;
      if (lastTime !== null && (now - lastTime) < minIntervalMs) {
        this._holoAnimFrame = requestAnimationFrame(renderLoop);
        return;
      }
      const elapsed = Math.max(0, now - startTime);
      const dt = Math.min(Math.max(0, (now - lastTime) / 1000), 0.1);
      lastTime = now;

      ctx.save();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(dpr, dpr);

      const w = cssW;
      const h = cssH;
      const cx = w * 0.5;
      const cy = h * 0.5;

      // ── HOLO-STAGE MATERIALIZATION BOOT-IN (out of the EMP burst) ──
      const bootProgress = Math.min(1.0, elapsed / 300);
      const bootScale = (elapsed < 300)
        ? (0.84 + 0.16 * Math.sin(bootProgress * Math.PI * 0.5))
        : 1.0;
      const bootAlpha = Math.min(1.0, elapsed / 180);

      ctx.save();
      if (bootScale !== 1.0) {
        ctx.translate(cx, cy);
        ctx.scale(bootScale, bootScale);
        ctx.translate(-cx, -cy);
      }
      ctx.globalAlpha = bootAlpha;

      if (result.isDuplicate) {
        // --- DUPLICATE REFUND SHOWCASE ---
        // Ambient golden bloom
        const ambientGrad = ctx.createRadialGradient(cx, cy, 10, cx, cy, Math.max(160, w * 0.4));
        ambientGrad.addColorStop(0, 'rgba(255, 204, 0, 0.22)');
        ambientGrad.addColorStop(0.6, 'rgba(249, 115, 22, 0.08)');
        ambientGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = ambientGrad;
        ctx.fillRect(0, 0, w, h);

        // Exploding & Curving Fugazi Hexagons
        for (const p of particles) {
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          p.vy += 320 * dt; // gravity
          p.rot += p.vRot * dt;

          const pAlpha = Math.max(0, 1 - (elapsed / 1900) * 0.85);
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.globalAlpha = pAlpha;
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 2.5;
          ctx.shadowColor = p.color;
          ctx.shadowBlur = 14;
          ctx.fillStyle = p.color === '#ffcc00' ? 'rgba(255, 204, 0, 0.28)' : 'rgba(249, 115, 22, 0.28)';

          ctx.beginPath();
          for (let s = 0; s < 6; s++) {
            const a = (s * Math.PI) / 3;
            const px = Math.cos(a) * (p.size * 0.5);
            const py = Math.sin(a) * (p.size * 0.5);
            if (s === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
          ctx.fill();
          ctx.stroke();

          // Center spark
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(0, 0, 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        // Central Holographic Recycled Badge
        ctx.save();
        ctx.translate(cx, cy);
        const pulse = 1.0 + 0.03 * Math.sin(elapsed * 0.008);
        ctx.scale(pulse, pulse);

        ctx.fillStyle = 'rgba(15, 23, 42, 0.9)';
        ctx.strokeStyle = '#ffcc00';
        ctx.lineWidth = 3;
        ctx.shadowColor = '#ffcc00';
        ctx.shadowBlur = 24;

        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(-badgeW * 0.5, -badgeH * 0.5, badgeW, badgeH, 12);
        else ctx.rect(-badgeW * 0.5, -badgeH * 0.5, badgeW, badgeH);
        ctx.fill();
        ctx.stroke();

        ctx.font = `900 ${subFontSize}px "Share Tech Mono", monospace`;
        ctx.fillStyle = '#f97316';
        ctx.shadowColor = '#f97316';
        ctx.shadowBlur = 10;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(subText, 0, -14);

        ctx.font = `900 ${refundFontSize}px "Orbitron", sans-serif`;
        ctx.fillStyle = '#ffcc00';
        ctx.shadowColor = '#ffcc00';
        ctx.shadowBlur = 18;
        ctx.fillText(refundText, 0, 14);
        ctx.restore();

        // Ambient sparkling motes floating around the badge for extended display
        for (let s = 0; s < 6; s++) {
          const sPhase = (elapsed * 0.0012 + s * 0.16) % 1.0;
          const sX = cx + Math.sin(s * 2.8 + elapsed * 0.004) * (badgeW * 0.45);
          const sY = (cy + 25) - sPhase * 60;
          const sAlpha = Math.sin(sPhase * Math.PI) * 0.8;
          ctx.fillStyle = '#ffcc00';
          ctx.shadowColor = '#ffcc00';
          ctx.shadowBlur = 8;
          ctx.globalAlpha = Math.max(0, sAlpha);
          ctx.fillRect(sX - 2, sY - 2, 4, 4);
        }
        ctx.globalAlpha = 1;

      } else {
        // --- NEW COSMETIC UNLOCKED SHOWCASE ---
        const styleHex = result.style?.hex || '#00f0ff';
        const attrId = result.attribute?.id;

        // Ambient radial glow in style hex
        const ambientGrad = ctx.createRadialGradient(cx, cy, 10, cx, cy, Math.max(160, w * 0.45));
        ambientGrad.addColorStop(0, `${styleHex}33`);
        ambientGrad.addColorStop(0.7, `${styleHex}0d`);
        ambientGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = ambientGrad;
        ctx.fillRect(0, 0, w, h);

        if (attrId === 'frame') {
          // 🖼️ CARD FRAME SHOWCASE (Permanent Holographic Foil Luster & Continuous Dual-Band Sheen)
          ctx.save();
          ctx.translate(cx, cy);
          const cardTilt = Math.sin(elapsed * 0.003) * 0.035;
          ctx.rotate(cardTilt);
          const cw = Math.min(w * 0.42, 160);
          const ch = Math.min(h * 0.72, 220);

          // Rhythmic cyber glow breathing pulse
          const framePulse = 0.5 + 0.5 * Math.sin(elapsed * 0.0038);
          const chassisGlowBlur = 20 + 16 * framePulse;
          const chassisLineWidth = 3.0 + 1.2 * framePulse;

          // Outer Card Chassis
          ctx.fillStyle = 'rgba(10, 16, 28, 0.96)';
          ctx.strokeStyle = styleHex;
          ctx.lineWidth = chassisLineWidth;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = chassisGlowBlur;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(-cw * 0.5, -ch * 0.5, cw, ch, 10);
          else ctx.rect(-cw * 0.5, -ch * 0.5, cw, ch);
          ctx.fill();
          ctx.stroke();

          // Corner Brackets / Notches
          ctx.strokeStyle = styleHex;
          ctx.lineWidth = 2.2;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = chassisGlowBlur * 0.65;
          const notchSize = 12;
          // Top-left
          ctx.beginPath();
          ctx.moveTo(-cw * 0.5 + 4, -ch * 0.5 + 4 + notchSize);
          ctx.lineTo(-cw * 0.5 + 4, -ch * 0.5 + 4);
          ctx.lineTo(-cw * 0.5 + 4 + notchSize, -ch * 0.5 + 4);
          ctx.stroke();
          // Top-right
          ctx.beginPath();
          ctx.moveTo(cw * 0.5 - 4 - notchSize, -ch * 0.5 + 4);
          ctx.lineTo(cw * 0.5 - 4, -ch * 0.5 + 4);
          ctx.lineTo(cw * 0.5 - 4, -ch * 0.5 + 4 + notchSize);
          ctx.stroke();
          // Bottom-left
          ctx.beginPath();
          ctx.moveTo(-cw * 0.5 + 4, ch * 0.5 - 4 - notchSize);
          ctx.lineTo(-cw * 0.5 + 4, ch * 0.5 - 4);
          ctx.lineTo(-cw * 0.5 + 4 + notchSize, ch * 0.5 - 4);
          ctx.stroke();
          // Bottom-right
          ctx.beginPath();
          ctx.moveTo(cw * 0.5 - 4 - notchSize, ch * 0.5 - 4);
          ctx.lineTo(cw * 0.5 - 4, ch * 0.5 - 4);
          ctx.lineTo(cw * 0.5 - 4, ch * 0.5 - 4 - notchSize);
          ctx.stroke();

          // Inner Artwork Aperture
          const apW = cw - 20;
          const apH = ch - 20;
          ctx.strokeStyle = styleHex;
          ctx.lineWidth = 1.8;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = 10;
          ctx.strokeRect(-apW * 0.5, -apH * 0.5, apW, apH);

          // Subtle horizontal aperture scanlines
          ctx.save();
          ctx.beginPath();
          ctx.rect(-apW * 0.5, -apH * 0.5, apW, apH);
          ctx.clip();
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.04)';
          ctx.lineWidth = 1;
          for (let sy = -apH * 0.5 + 4; sy < apH * 0.5; sy += 5) {
            ctx.beginPath();
            ctx.moveTo(-apW * 0.5, sy);
            ctx.lineTo(apW * 0.5, sy);
            ctx.stroke();
          }

          // Top Header Pill Badge: "FRAME" (No "GLOBAL")
          ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
          ctx.strokeStyle = styleHex;
          ctx.lineWidth = 1;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = 6;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(-32, -ch * 0.5 + 15, 64, 18, 4);
          else ctx.rect(-32, -ch * 0.5 + 15, 64, 18);
          ctx.fill();
          ctx.stroke();

          ctx.font = '900 10.5px "Orbitron", sans-serif';
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('FRAME', 0, -ch * 0.5 + 24);

          // Central Cyber Reticle & Emblem
          ctx.strokeStyle = `${styleHex}44`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(-28, 0); ctx.lineTo(-12, 0);
          ctx.moveTo(12, 0);  ctx.lineTo(28, 0);
          ctx.moveTo(0, -28); ctx.lineTo(0, -12);
          ctx.moveTo(0, 12);  ctx.lineTo(0, 28);
          ctx.stroke();

          const crestBreath = 1.0 + 0.05 * Math.sin(elapsed * 0.004);
          ctx.font = '900 32px "Orbitron", sans-serif';
          ctx.fillStyle = styleHex;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = 18;
          ctx.save();
          ctx.scale(crestBreath, crestBreath);
          ctx.fillText('◈', 0, 0);
          ctx.restore();

          // ── PERMANENT HOLOGRAPHIC FOIL SHIMMER & CONTINUOUS SPECULAR SWEEP ──
          // 1. Permanent Ambient Prismatic Foil Luster (Always present, shifts iridescent colors)
          const shimmerAngle = (elapsed * 0.00075) % (Math.PI * 2);
          const foilGrad = ctx.createLinearGradient(
            -apW * 0.5 * Math.cos(shimmerAngle), -apH * 0.5 * Math.sin(shimmerAngle),
            apW * 0.5 * Math.cos(shimmerAngle), apH * 0.5 * Math.sin(shimmerAngle)
          );
          foilGrad.addColorStop(0, `${styleHex}14`);
          foilGrad.addColorStop(0.28, 'rgba(255, 255, 255, 0.08)');
          foilGrad.addColorStop(0.5, `${styleHex}2c`);
          foilGrad.addColorStop(0.72, 'rgba(255, 255, 255, 0.12)');
          foilGrad.addColorStop(1, `${styleHex}12`);
          ctx.fillStyle = foilGrad;
          ctx.fillRect(-apW * 0.5, -apH * 0.5, apW, apH);

          // 2. Seamless Continuous Dual-Band Specular Sheen (No dead pauses, never disappears!)
          for (let b = 0; b < 2; b++) {
            const bandProgress = ((elapsed * 0.00045 + b * 0.5) % 1.0);
            const beamX = -apW * 0.85 + bandProgress * (apW * 1.7);
            const bandWidth = apW * 0.45;
            const sheenGrad = ctx.createLinearGradient(
              beamX - bandWidth * 0.5, -apH * 0.5,
              beamX + bandWidth * 0.5, apH * 0.5
            );
            sheenGrad.addColorStop(0, 'rgba(255, 255, 255, 0)');
            sheenGrad.addColorStop(0.3, `${styleHex}24`);
            sheenGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.38)');
            sheenGrad.addColorStop(0.7, `${styleHex}24`);
            sheenGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
            ctx.fillStyle = sheenGrad;
            ctx.fillRect(-apW * 0.5, -apH * 0.5, apW, apH);
          }

          // 3. Subtle micro glint sparkles drifting on the frame surface
          for (let sp = 0; sp < 4; sp++) {
            const spPhase = ((elapsed * 0.0014 + sp * 0.25) % 1.0);
            const spAlpha = Math.sin(spPhase * Math.PI) * 0.85;
            const spX = -apW * 0.38 + ((sp * 43) % 76) * (apW * 0.01);
            const spY = -apH * 0.38 + ((sp * 59) % 76) * (apH * 0.01);
            ctx.fillStyle = '#ffffff';
            ctx.shadowColor = styleHex;
            ctx.shadowBlur = 6;
            ctx.globalAlpha = Math.max(0, spAlpha);
            ctx.beginPath();
            ctx.arc(spX, spY, 1.2, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalAlpha = 1.0;

          // Bottom Decorative Circuit Line
          ctx.strokeStyle = `${styleHex}88`;
          ctx.lineWidth = 1.5;
          ctx.shadowBlur = 0;
          ctx.beginPath();
          ctx.moveTo(-apW * 0.35, apH * 0.5 - 12);
          ctx.lineTo(apW * 0.35, apH * 0.5 - 12);
          ctx.stroke();

          ctx.restore();
          ctx.restore();

        } else if (attrId === 'hp_bar_color') {
          // 🩸 ANIMATED HP BAR MONITOR (Segmented pulse, scan glint & live telemetry)
          ctx.save();
          ctx.translate(cx, cy);
          const bw = Math.min(w * 0.84, 340);
          const bh = 64;

          // Main Chassis
          ctx.fillStyle = 'rgba(10, 16, 28, 0.95)';
          ctx.strokeStyle = '#334155';
          ctx.lineWidth = 2;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(-bw * 0.5, -bh * 0.5, bw, bh, 8);
          else ctx.rect(-bw * 0.5, -bh * 0.5, bw, bh);
          ctx.fill();
          ctx.stroke();

          // Corner Accent Brackets
          ctx.strokeStyle = styleHex;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(-bw * 0.5 + 4, -bh * 0.5 + 12);
          ctx.lineTo(-bw * 0.5 + 4, -bh * 0.5 + 4);
          ctx.lineTo(-bw * 0.5 + 12, -bh * 0.5 + 4);
          ctx.moveTo(bw * 0.5 - 4, bh * 0.5 - 12);
          ctx.lineTo(bw * 0.5 - 4, bh * 0.5 - 4);
          ctx.lineTo(bw * 0.5 - 12, bh * 0.5 - 4);
          ctx.stroke();

          // Dynamic Fill Breathing & Live Telemetry
          const fillRatio = 0.82 + 0.07 * Math.sin(elapsed * 0.003);
          const hpVal = Math.round(fillRatio * 1000);

          // Status Dot (pulsing)
          const dotPulse = 0.6 + 0.4 * Math.sin(elapsed * 0.006);
          ctx.fillStyle = styleHex;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = 10 * dotPulse;
          ctx.beginPath();
          ctx.arc(-bw * 0.5 + 18, -bh * 0.5 + 18, 3.5, 0, Math.PI * 2);
          ctx.fill();

          ctx.font = '900 11px "Share Tech Mono", monospace';
          ctx.fillStyle = '#94a3b8';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText('VITAL MONITOR', -bw * 0.5 + 28, -bh * 0.5 + 18);

          // Numeric Telemetry
          ctx.textAlign = 'right';
          ctx.fillStyle = styleHex;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = 10;
          ctx.fillText(`${hpVal} / 1000 HP`, bw * 0.5 - 18, -bh * 0.5 + 18);

          // Recessed Bar Track
          const barW = bw - 36;
          const barH = 20;
          const bx = -bw * 0.5 + 18;
          const by = -bh * 0.5 + 29;
          ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
          ctx.fillRect(bx, by, barW, barH);
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
          ctx.strokeRect(bx, by, barW, barH);

          // 16 Segmented Energy Cells
          const segments = 16;
          const segGap = 2;
          const segW = (barW - (segments - 1) * segGap) / segments;
          const filledSegs = Math.floor(segments * fillRatio);
          const partialRatio = (segments * fillRatio) - filledSegs;

          for (let s = 0; s < segments; s++) {
            const sx = bx + s * (segW + segGap);
            if (s < filledSegs) {
              ctx.fillStyle = styleHex;
              ctx.shadowColor = styleHex;
              ctx.shadowBlur = 14;
              ctx.fillRect(sx, by + 1, segW, barH - 2);
            } else if (s === filledSegs && partialRatio > 0) {
              ctx.fillStyle = styleHex;
              ctx.shadowColor = styleHex;
              ctx.shadowBlur = 10;
              ctx.fillRect(sx, by + 1, segW * partialRatio, barH - 2);
            } else {
              ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
              ctx.shadowBlur = 0;
              ctx.fillRect(sx, by + 1, segW, barH - 2);
            }
          }

          // Laser Glint Sweep across health bar
          const scanNorm = (elapsed * 0.0006) % 1.5;
          if (scanNorm <= 1.0) {
            const scanX = bx + scanNorm * (barW * fillRatio);
            const scanGrad = ctx.createLinearGradient(scanX - 16, by, scanX + 16, by);
            scanGrad.addColorStop(0, 'rgba(255, 255, 255, 0)');
            scanGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.85)');
            scanGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
            ctx.fillStyle = scanGrad;
            ctx.fillRect(scanX - 16, by + 1, 32, barH - 2);
          }

          ctx.restore();

        } else if (attrId === 'avatar_border') {
          // 👤 AVATAR BORDER
          ctx.save();
          ctx.translate(cx, cy);
          const avR = 54;
          for (let a = 0; a < 14; a++) {
            const ang = elapsed * 0.003 + (a * Math.PI * 2) / 14;
            const px = Math.cos(ang) * (avR + 10);
            const py = Math.sin(ang) * (avR + 10);
            ctx.fillStyle = styleHex;
            ctx.shadowColor = styleHex;
            ctx.shadowBlur = 12;
            ctx.beginPath();
            ctx.arc(px, py, 3, 0, Math.PI * 2);
            ctx.fill();
          }

          ctx.beginPath();
          ctx.arc(0, 0, avR, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(15, 23, 42, 0.95)';
          ctx.fill();
          ctx.strokeStyle = styleHex;
          ctx.lineWidth = 4;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = 24;
          ctx.stroke();

          ctx.font = '38px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('👤', 0, 0);
          ctx.restore();

        } else if (attrId === 'deck_color') {
          // 🎨 DECK COLOR SHOWCASE (Arsenal > Player > Deck Color preview slab with ◈ crest)
          ctx.save();
          ctx.translate(cx, cy);
          const dw = Math.min(w * 0.42, 140);
          const dh = Math.min(h * 0.70, 205);
          const hoverY = Math.sin(elapsed * 0.003) * 4;

          // 3D Deck Slab Stack: Bottom & Middle card depth offsets
          ctx.save();
          ctx.translate(8, 8 + hoverY);
          ctx.fillStyle = 'rgba(5, 10, 18, 0.75)';
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(-dw * 0.5, -dh * 0.5, dw, dh, 10);
          else ctx.rect(-dw * 0.5, -dh * 0.5, dw, dh);
          ctx.fill();
          ctx.stroke();
          ctx.restore();

          ctx.save();
          ctx.translate(4, 4 + hoverY);
          ctx.fillStyle = 'rgba(9, 18, 32, 0.88)';
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(-dw * 0.5, -dh * 0.5, dw, dh, 10);
          else ctx.rect(-dw * 0.5, -dh * 0.5, dw, dh);
          ctx.fill();
          ctx.stroke();
          ctx.restore();

          // Top Main Deck Card Slab
          ctx.save();
          ctx.translate(0, hoverY);

          // Card face background (Linear gradient: #0f1c2e -> #091322 -> #050a12)
          if (!_cachedSlotFaceGrad) {
            _cachedSlotFaceGrad = ctx.createLinearGradient(-dw * 0.5, -dh * 0.5, dw * 0.5, dh * 0.5);
            _cachedSlotFaceGrad.addColorStop(0, '#0f1c2e');
            _cachedSlotFaceGrad.addColorStop(0.55, '#091322');
            _cachedSlotFaceGrad.addColorStop(1, '#050a12');
          }
          ctx.fillStyle = _cachedSlotFaceGrad;
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
          ctx.lineWidth = 1.5;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = 18;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(-dw * 0.5, -dh * 0.5, dw, dh, 10);
          else ctx.rect(-dw * 0.5, -dh * 0.5, dw, dh);
          ctx.fill();
          ctx.stroke();

          // Inset Bezel (.deck-card-bezel: 6px inset, 7px radius)
          const bzInset = 6;
          const bzW = dw - bzInset * 2;
          const bzH = dh - bzInset * 2;
          ctx.strokeStyle = `${styleHex}44`;
          ctx.lineWidth = 1;
          ctx.shadowBlur = 0;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(-bzW * 0.5, -bzH * 0.5, bzW, bzH, 7);
          else ctx.rect(-bzW * 0.5, -bzH * 0.5, bzW, bzH);
          ctx.stroke();

          // Corner circuit notches
          ctx.strokeStyle = `${styleHex}66`;
          ctx.lineWidth = 1.2;
          const cLen = 10;
          ctx.beginPath();
          // Top-left
          ctx.moveTo(-bzW * 0.5 + 4, -bzH * 0.5 + 4 + cLen);
          ctx.lineTo(-bzW * 0.5 + 4, -bzH * 0.5 + 4);
          ctx.lineTo(-bzW * 0.5 + 4 + cLen, -bzH * 0.5 + 4);
          // Top-right
          ctx.moveTo(bzW * 0.5 - 4 - cLen, -bzH * 0.5 + 4);
          ctx.lineTo(bzW * 0.5 - 4, -bzH * 0.5 + 4);
          ctx.lineTo(bzW * 0.5 - 4, -bzH * 0.5 + 4 + cLen);
          // Bottom-left
          ctx.moveTo(-bzW * 0.5 + 4, bzH * 0.5 - 4 - cLen);
          ctx.lineTo(-bzW * 0.5 + 4, bzH * 0.5 - 4);
          ctx.lineTo(-bzW * 0.5 + 4 + cLen, bzH * 0.5 - 4);
          // Bottom-right
          ctx.moveTo(bzW * 0.5 - 4 - cLen, bzH * 0.5 - 4);
          ctx.lineTo(bzW * 0.5 - 4, bzH * 0.5 - 4);
          ctx.lineTo(bzW * 0.5 - 4, bzH * 0.5 - 4 - cLen);
          ctx.stroke();

          // Center Glowing Cyber Crest (◈)
          const crestPulse = 1.0 + 0.04 * Math.sin(elapsed * 0.004);
          ctx.save();
          ctx.scale(crestPulse, crestPulse);
          ctx.font = '900 36px "Orbitron", sans-serif';
          ctx.fillStyle = styleHex;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = 24;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('◈', 0, 0);
          ctx.restore();

          // Front Glass Diagonal Sheen
          ctx.save();
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(-dw * 0.5, -dh * 0.5, dw, dh, 10);
          else ctx.rect(-dw * 0.5, -dh * 0.5, dw, dh);
          ctx.clip();
          if (!_cachedSlotGlassGrad) {
            _cachedSlotGlassGrad = ctx.createLinearGradient(-dw * 0.5, -dh * 0.5, dw * 0.5, dh * 0.5);
            _cachedSlotGlassGrad.addColorStop(0, 'rgba(255, 255, 255, 0.12)');
            _cachedSlotGlassGrad.addColorStop(0.3, 'rgba(255, 255, 255, 0.04)');
            _cachedSlotGlassGrad.addColorStop(0.6, 'rgba(255, 255, 255, 0)');
          }
          ctx.fillStyle = _cachedSlotGlassGrad;
          ctx.fillRect(-dw * 0.5, -dh * 0.5, dw, dh);
          ctx.restore();

          ctx.restore();
          ctx.restore();

        } else if (attrId === 'tray_color') {
          // 🎴 CARD TRAY GLOW SHOWCASE (Pulsing dock chassis reward with 5 static docked cards)
          ctx.save();
          ctx.translate(cx, cy);
          const tw = Math.min(w * 0.88, 380);
          const th = 104;

          // Rhythmic breathing pulse for the unlocked tray dock chassis & neon bloom
          const trayPulse = 0.5 + 0.5 * Math.sin(elapsed * 0.004);
          const glowBlur = 18 + 22 * trayPulse;
          const glowWidth = 2.4 + 1.4 * trayPulse;
          const floorAlphaHex = Math.round((0.22 + 0.38 * trayPulse) * 255).toString(16).padStart(2, '0');

          // Recessed Hand Tray Dock Base
          ctx.fillStyle = 'rgba(8, 14, 24, 0.95)';
          ctx.strokeStyle = styleHex;
          ctx.lineWidth = glowWidth;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = glowBlur;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(-tw * 0.5, -th * 0.5, tw, th, 12);
          else ctx.rect(-tw * 0.5, -th * 0.5, tw, th);
          ctx.fill();
          ctx.stroke();

          // Ambient floor underglow on the tray surface (pulsing)
          let floorGrad = _cachedSlotFloorGradMap.get(styleHex);
          if (!floorGrad) {
            floorGrad = ctx.createRadialGradient(0, th * 0.5 - 12, 10, 0, th * 0.5 - 12, tw * 0.45);
            floorGrad.addColorStop(0, `${styleHex}ff`);
            floorGrad.addColorStop(0.7, `${styleHex}2b`);
            floorGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            _cachedSlotFloorGradMap.set(styleHex, floorGrad);
          }
          ctx.save();
          ctx.globalAlpha = 0.22 + 0.38 * trayPulse;
          ctx.fillStyle = floorGrad;
          ctx.fillRect(-tw * 0.5 + 4, -th * 0.5 + 4, tw - 8, th - 8);
          ctx.restore();

          // Outer Dock Chassis Corner Bracket Accents
          ctx.strokeStyle = styleHex;
          ctx.lineWidth = glowWidth;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = glowBlur * 0.8;
          const dockBracket = 14;
          // Top-left
          ctx.beginPath();
          ctx.moveTo(-tw * 0.5 + 3, -th * 0.5 + 3 + dockBracket);
          ctx.lineTo(-tw * 0.5 + 3, -th * 0.5 + 3);
          ctx.lineTo(-tw * 0.5 + 3 + dockBracket, -th * 0.5 + 3);
          // Top-right
          ctx.moveTo(tw * 0.5 - 3 - dockBracket, -th * 0.5 + 3);
          ctx.lineTo(tw * 0.5 - 3, -th * 0.5 + 3);
          ctx.lineTo(tw * 0.5 - 3, -th * 0.5 + 3 + dockBracket);
          // Bottom-left
          ctx.moveTo(-tw * 0.5 + 3, th * 0.5 - 3 - dockBracket);
          ctx.lineTo(-tw * 0.5 + 3, th * 0.5 - 3);
          ctx.lineTo(-tw * 0.5 + 3 + dockBracket, th * 0.5 - 3);
          // Bottom-right
          ctx.moveTo(tw * 0.5 - 3 - dockBracket, th * 0.5 - 3);
          ctx.lineTo(tw * 0.5 - 3, th * 0.5 - 3);
          ctx.lineTo(tw * 0.5 - 3, th * 0.5 - 3 - dockBracket);
          ctx.stroke();

          // Bottom dock neon guide line
          ctx.strokeStyle = styleHex;
          ctx.lineWidth = glowWidth * 0.8;
          ctx.shadowColor = styleHex;
          ctx.shadowBlur = glowBlur * 0.65;
          ctx.beginPath();
          ctx.moveTo(-tw * 0.45, th * 0.5 - 10);
          ctx.lineTo(tw * 0.45, th * 0.5 - 10);
          ctx.stroke();

          // 5 Symbolic Hand Card Frames (STATIC docked cards)
          const cardCount = 5;
          const cardW = Math.min((tw - 36) / cardCount - 6, 48);
          const cardH = 70;
          const spacing = cardW + 8;
          const startX = -((cardCount - 1) * spacing) * 0.5;

          for (let i = 0; i < cardCount; i++) {
            const cardX = startX + i * spacing;
            const cardY = -3; // Static cards: focus remains cleanly on the glowing tray frame!

            ctx.save();
            ctx.translate(cardX, cardY);

            // Card Body (Subtle styling so glowing tray frame stands out)
            ctx.fillStyle = 'rgba(15, 23, 42, 0.96)';
            ctx.strokeStyle = `${styleHex}66`;
            ctx.lineWidth = 1.4;
            ctx.shadowBlur = 0;
            ctx.beginPath();
            if (ctx.roundRect) ctx.roundRect(-cardW * 0.5, -cardH * 0.5, cardW, cardH, 5);
            else ctx.rect(-cardW * 0.5, -cardH * 0.5, cardW, cardH);
            ctx.fill();
            ctx.stroke();

            // Mini Card Header Bar
            ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
            ctx.shadowBlur = 0;
            ctx.fillRect(-cardW * 0.5 + 3, -cardH * 0.5 + 4, cardW - 6, 8);

            // Center Micro Cyber Crest
            ctx.font = '12px "Orbitron", sans-serif';
            ctx.fillStyle = `${styleHex}bb`;
            ctx.shadowBlur = 0;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('◈', 0, 4);

            // Bottom Micro Stat Bracket
            ctx.strokeStyle = `${styleHex}55`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(-cardW * 0.5 + 6, cardH * 0.5 - 6);
            ctx.lineTo(cardW * 0.5 - 6, cardH * 0.5 - 6);
            ctx.stroke();

            ctx.restore();
          }

          ctx.restore();

        } else if (attrId === 'taunt') {
          // 💬 COMMANDER TAUNT (In-game Tactical Comms Action Cut-In Banner)
          ctx.save();
          ctx.translate(cx, cy);
          const tw = Math.min(w * 0.92, 380);
          const th = 84;
          const tauntDef = result.taunt || {};
          const tauntColor = tauntDef.themeColor || '#00f0ff';
          const callout = (tauntDef.callout || 'TACTICAL COMMS').toUpperCase();
          const tauntText = (tauntDef.text || tauntDef.label || 'TAUNT').toUpperCase();
          const accentIcon = tauntDef.accentIcon || '⚡';
          const tauntIcon = tauntDef.icon || '💬';

          // Slanted Chamfered Combat Banner Path
          const slant = 28;
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(-tw * 0.5, -th * 0.5);
          ctx.lineTo(tw * 0.5, -th * 0.5);
          ctx.lineTo(tw * 0.5 - slant, th * 0.5);
          ctx.lineTo(-tw * 0.5, th * 0.5);
          ctx.closePath();
          ctx.clip();

          // Dark High-Tech Background Gradient
          let bannerGrad = _cachedSlotBannerGradMap.get(tauntColor);
          if (!bannerGrad) {
            bannerGrad = ctx.createLinearGradient(-tw * 0.5, 0, tw * 0.5, 0);
            bannerGrad.addColorStop(0, 'rgba(2, 8, 20, 0.96)');
            bannerGrad.addColorStop(0.75, 'rgba(4, 18, 38, 0.92)');
            bannerGrad.addColorStop(1, `${tauntColor}22`);
            _cachedSlotBannerGradMap.set(tauntColor, bannerGrad);
          }
          ctx.fillStyle = bannerGrad;
          ctx.fillRect(-tw * 0.5, -th * 0.5, tw, th);

          // Horizontal Speedlines
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.04)';
          ctx.lineWidth = 1.5;
          for (let ly = -th * 0.5; ly < th * 0.5; ly += 4) {
            ctx.beginPath();
            ctx.moveTo(-tw * 0.5, ly);
            ctx.lineTo(tw * 0.5, ly);
            ctx.stroke();
          }
          ctx.restore();

          // Top and Bottom Neon Laser Rails
          ctx.strokeStyle = tauntColor;
          ctx.lineWidth = 2.5;
          ctx.shadowColor = tauntColor;
          ctx.shadowBlur = 20;
          // Top Rail
          ctx.beginPath();
          ctx.moveTo(-tw * 0.5, -th * 0.5);
          ctx.lineTo(tw * 0.5, -th * 0.5);
          ctx.stroke();
          // Bottom Rail
          ctx.beginPath();
          ctx.moveTo(-tw * 0.5, th * 0.5);
          ctx.lineTo(tw * 0.5 - slant, th * 0.5);
          ctx.stroke();
          // Slanted End Rail
          ctx.beginPath();
          ctx.moveTo(tw * 0.5, -th * 0.5);
          ctx.lineTo(tw * 0.5 - slant, th * 0.5);
          ctx.stroke();

          // Left Panel: Commander Close-Up Avatar Box (54x54)
          const avX = -tw * 0.5 + 38;
          const avY = 0;
          const avSize = 54;
          ctx.fillStyle = 'rgba(2, 6, 14, 0.98)';
          ctx.strokeStyle = tauntColor;
          ctx.lineWidth = 2;
          ctx.shadowColor = tauntColor;
          ctx.shadowBlur = 14;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(avX - avSize * 0.5, avY - avSize * 0.5, avSize, avSize, 4);
          else ctx.rect(avX - avSize * 0.5, avY - avSize * 0.5, avSize, avSize);
          ctx.fill();
          ctx.stroke();

          // Avatar Icon / Portrait
          ctx.font = '26px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(tauntIcon, avX, avY);

          // Content Column
          const contentX = avX + avSize * 0.5 + 16;
          const availW = (tw * 0.5 - slant) - contentX;
          const textCenterX = contentX + availW * 0.5;

          // Callout Tag ("TACTICAL COMMS", etc.)
          ctx.font = '900 11px "Share Tech Mono", monospace';
          ctx.fillStyle = tauntColor;
          ctx.shadowColor = tauntColor;
          ctx.shadowBlur = 8;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(callout, textCenterX, -16);

          // Comic Onomatopoeia Main Text Row (Skewed italic Orbitron)
          ctx.save();
          ctx.translate(textCenterX, 14);
          ctx.transform(1, 0, -0.16, 1, 0, 0); // Comic dynamic skew

          ctx.font = 'italic 900 20px "Orbitron", sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const comicText = `${accentIcon}  "${tauntText}"  ${accentIcon}`;

          // Black comic outline
          ctx.lineWidth = 3.5;
          ctx.strokeStyle = '#000000';
          ctx.shadowBlur = 0;
          ctx.strokeText(comicText, 0, 0);

          // Glowing white text fill
          ctx.fillStyle = '#ffffff';
          ctx.shadowColor = tauntColor;
          ctx.shadowBlur = 16;
          ctx.fillText(comicText, 0, 0);
          ctx.restore();

          // Anamorphic Lens Flare Sweep
          const flareNorm = (elapsed * 0.0006) % 2.2;
          if (flareNorm <= 1.2) {
            const flareX = -tw * 0.5 + flareNorm * tw;
            const flareGrad = ctx.createLinearGradient(flareX - 50, 0, flareX + 50, 0);
            flareGrad.addColorStop(0, 'rgba(255, 255, 255, 0)');
            flareGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.7)');
            flareGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
            ctx.fillStyle = flareGrad;
            ctx.fillRect(flareX - 50, -th * 0.5 + 2, 100, th - 4);
          }

          ctx.restore();

        } else if (attrId === 'profile_artwork') {
          // 👤 PROFILE ARTWORK (Permanent Holographic Prismatic Luster & Continuous Dual-Band Sheen)
          ctx.save();
          ctx.translate(cx, cy);

          // Subtle floating bob and levitation tilt
          const cardTilt = Math.sin(elapsed * 0.0028) * 0.03;
          const cardBob = Math.sin(elapsed * 0.0035) * 3.5;
          ctx.translate(0, cardBob);
          ctx.rotate(cardTilt);

          const aw = Math.min(w * 0.38, 140);
          const ah = Math.min(h * 0.65, 190);
          const artImg = this._getCachedImage(getCardThumbUrl(result.artwork?.image, 'm') || getCardThumbUrl(result.artwork?.image, 's'));

          const profileColor = '#00f0ff';
          const glowPulse = 0.5 + 0.5 * Math.sin(elapsed * 0.0038);
          const frameBlur = 20 + 16 * glowPulse;
          const frameWidth = 2.8 + 1.2 * glowPulse;

          if (artImg && artImg.complete) {
            ctx.save();
            ctx.beginPath();
            if (ctx.roundRect) ctx.roundRect(-aw * 0.5, -ah * 0.5, aw, ah, 8);
            else ctx.rect(-aw * 0.5, -ah * 0.5, aw, ah);
            ctx.clip();
            ctx.drawImage(artImg, -aw * 0.5, -ah * 0.5, aw, ah);

            // 1. Permanent Ambient Iridescent Foil Luster (Always present across entire card)
            const holoAngle = (elapsed * 0.00075) % (Math.PI * 2);
            const baseGrad = ctx.createLinearGradient(
              -aw * 0.5 * Math.cos(holoAngle), -ah * 0.5 * Math.sin(holoAngle),
              aw * 0.5 * Math.cos(holoAngle), ah * 0.5 * Math.sin(holoAngle)
            );
            baseGrad.addColorStop(0, 'rgba(0, 240, 255, 0.12)');
            baseGrad.addColorStop(0.25, 'rgba(236, 72, 153, 0.10)');
            baseGrad.addColorStop(0.5, 'rgba(254, 240, 138, 0.14)');
            baseGrad.addColorStop(0.75, 'rgba(168, 85, 247, 0.10)');
            baseGrad.addColorStop(1, 'rgba(0, 240, 255, 0.12)');
            ctx.fillStyle = baseGrad;
            ctx.fillRect(-aw * 0.5, -ah * 0.5, aw, ah);

            // 2. Seamless Continuous Dual-Band Specular Sheen (No dead pauses, never disappears!)
            for (let b = 0; b < 2; b++) {
              const bandProgress = ((elapsed * 0.00045 + b * 0.5) % 1.0);
              const beamX = -aw * 0.85 + bandProgress * (aw * 1.7);
              const bandWidth = aw * 0.45;
              const sheenGrad = ctx.createLinearGradient(
                beamX - bandWidth * 0.5, -ah * 0.5,
                beamX + bandWidth * 0.5, ah * 0.5
              );
              sheenGrad.addColorStop(0, 'rgba(255, 255, 255, 0)');
              sheenGrad.addColorStop(0.3, 'rgba(0, 240, 255, 0.22)');
              sheenGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.45)');
              sheenGrad.addColorStop(0.7, 'rgba(0, 240, 255, 0.22)');
              sheenGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
              ctx.fillStyle = sheenGrad;
              ctx.fillRect(-aw * 0.5, -ah * 0.5, aw, ah);
            }

            // 3. Floating micro holographic glints
            for (let sp = 0; sp < 4; sp++) {
              const spPhase = ((elapsed * 0.0014 + sp * 0.25) % 1.0);
              const spAlpha = Math.sin(spPhase * Math.PI) * 0.85;
              const spX = -aw * 0.38 + ((sp * 41) % 76) * (aw * 0.01);
              const spY = -ah * 0.38 + ((sp * 57) % 76) * (ah * 0.01);
              ctx.fillStyle = '#ffffff';
              ctx.shadowColor = '#00f0ff';
              ctx.shadowBlur = 6;
              ctx.globalAlpha = Math.max(0, spAlpha);
              ctx.beginPath();
              ctx.arc(spX, spY, 1.2, 0, Math.PI * 2);
              ctx.fill();
            }
            ctx.globalAlpha = 1.0;
            ctx.restore();
          }

          // Outer glowing holographic cyber border (pulsing bloom)
          ctx.strokeStyle = profileColor;
          ctx.lineWidth = frameWidth;
          ctx.shadowColor = profileColor;
          ctx.shadowBlur = frameBlur;
          if (ctx.roundRect) {
            ctx.beginPath();
            ctx.roundRect(-aw * 0.5, -ah * 0.5, aw, ah, 8);
            ctx.stroke();
          } else {
            ctx.strokeRect(-aw * 0.5, -ah * 0.5, aw, ah);
          }

          // Corner Cyber Accents on the Card
          const cLen = 10;
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1.6;
          ctx.shadowColor = profileColor;
          ctx.shadowBlur = 8;
          ctx.beginPath();
          // Top-left
          ctx.moveTo(-aw * 0.5 + 4, -ah * 0.5 + 4 + cLen);
          ctx.lineTo(-aw * 0.5 + 4, -ah * 0.5 + 4);
          ctx.lineTo(-aw * 0.5 + 4 + cLen, -ah * 0.5 + 4);
          // Top-right
          ctx.moveTo(aw * 0.5 - 4 - cLen, -ah * 0.5 + 4);
          ctx.lineTo(aw * 0.5 - 4, -ah * 0.5 + 4);
          ctx.lineTo(aw * 0.5 - 4, -ah * 0.5 + 4 + cLen);
          // Bottom-left
          ctx.moveTo(-aw * 0.5 + 4, ah * 0.5 - 4 - cLen);
          ctx.lineTo(-aw * 0.5 + 4, ah * 0.5 - 4);
          ctx.lineTo(-aw * 0.5 + 4 + cLen, ah * 0.5 - 4);
          // Bottom-right
          ctx.moveTo(aw * 0.5 - 4 - cLen, ah * 0.5 - 4);
          ctx.lineTo(aw * 0.5 - 4, ah * 0.5 - 4);
          ctx.lineTo(aw * 0.5 - 4, ah * 0.5 - 4 - cLen);
          ctx.stroke();

          ctx.restore();

        } else if (attrId === 'deckbuilder_card' || attrId === 'card_theme') {
          // 💎 DECKBUILDER / CARDS CHASSIS (Levitating 3D Card Hologram)
          ctx.save();
          ctx.translate(cx, cy);

          const cardBob = Math.sin(elapsed * 0.0035) * 4;
          ctx.translate(0, cardBob);

          const aw = Math.min(w * 0.38, 140);
          const ah = Math.min(h * 0.65, 190);
          const isCardsCat = (attrId === 'card_theme');
          const theme = isCardsCat ? result.cardTheme : result.deckbuilderTheme;
          const chassisColor = theme?.id === 'holo' ? '#c084fc' : (theme?.id === 'fluted' ? '#38bdf8' : (theme?.id === 'glass' ? '#00f0ff' : (theme?.id === 'artwork' ? '#f43f5e' : '#94a3b8')));

          // Base Chassis Plate
          ctx.fillStyle = '#060a12';
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(-aw * 0.5, -ah * 0.5, aw, ah, 12);
          else ctx.rect(-aw * 0.5, -ah * 0.5, aw, ah);
          ctx.fill();

          // Border & Glow
          ctx.strokeStyle = chassisColor;
          ctx.lineWidth = 2.5;
          ctx.shadowColor = chassisColor;
          ctx.shadowBlur = 18;
          ctx.stroke();

          // Hologram Text / Crest
          ctx.shadowBlur = 0;
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 12px Rajdhani, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText((theme?.name || 'CHASSIS').toUpperCase(), 0, -10);

          ctx.fillStyle = chassisColor;
          ctx.font = '9px Orbitron, sans-serif';
          ctx.fillText(isCardsCat ? 'CARDS' : 'DECK BUILDER', 0, 14);

          ctx.restore();

        } else if (attrId === 'deck_icon') {
          // 💎 DECK ICON / CREST INSIGNIA (Levitating 3D Insignia Hologram)
          ctx.save();
          ctx.translate(cx, cy);

          const iconBob = Math.sin(elapsed * 0.0035) * 4;
          ctx.translate(0, iconBob);

          const aw = Math.min(w * 0.38, 140);
          const ah = Math.min(h * 0.65, 190);
          const icon = result.deckIcon;
          const crestColor = icon?.rarity === 'epic' ? '#f59e0b' : '#c084fc';

          // Base Hologram Slab
          ctx.fillStyle = '#060a12';
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(-aw * 0.5, -ah * 0.5, aw, ah, 12);
          else ctx.rect(-aw * 0.5, -ah * 0.5, aw, ah);
          ctx.fill();

          // Border & Radiant Glow
          ctx.strokeStyle = crestColor;
          ctx.lineWidth = 2.5;
          ctx.shadowColor = crestColor;
          ctx.shadowBlur = 18;
          ctx.stroke();

          // Center Glyph / Crest Symbol
          ctx.shadowBlur = 12;
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 36px Orbitron, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(icon?.glyph || '✦', 0, -18);

          // Name and Subtitle
          ctx.shadowBlur = 0;
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 12px Rajdhani, sans-serif';
          ctx.fillText((icon?.label || 'DECK INSIGNIA').toUpperCase(), 0, 18);

          ctx.fillStyle = crestColor;
          ctx.font = '9px Orbitron, sans-serif';
          ctx.fillText('DECK ICON', 0, 36);

          ctx.restore();

        } else {
          // --- UNIT COSMETICS: bullet_color, unit_aura, rocket_trail ---
          const isTesla = (unitName.includes('Tesla') || unitType.includes('Tesla'));
          const isShade = (unitName.includes('Shade') || unitType.includes('Shade'));
          const isRocketship = (unitName.includes('Rocketship') || unitType.includes('Rocketship'));
          const isOrchestra = (unitName.includes('Orchestra') || unitType.includes('Orchestra'));
          const isApostate = (unitName.includes('Apostate') || unitType.includes('Apostate'));
          const isDelvingPillar = (unitName.includes('Delving Pillar') || unitType.includes('DelvingPillar'));
          const isPreserverLock = (unitName === 'Preserver Lock' || unitType === 'PreserverLock' || unitType === 'Preserver_Lock');
          const isSulRace = (race === 'Sul');

          if (attrId === 'unit_aura') {
            // ========================================================
            // 💫 UNIT AURA SHOWCASE: Centered Stage, Exact Silhouette
            // ========================================================
            const pedestalX = cx;
            const pedestalY = Math.round(h * 0.72);

            // 1. Holographic Pedestal with Concentric Pulse & Expanding Rings
            ctx.save();
            ctx.beginPath();
            ctx.ellipse(pedestalX, pedestalY, 96, 26, 0, 0, Math.PI * 2);
            ctx.strokeStyle = styleHex;
            ctx.lineWidth = 2.5;
            ctx.shadowColor = styleHex;
            ctx.shadowBlur = 18;
            ctx.stroke();
            ctx.fillStyle = `${styleHex}20`;
            ctx.fill();

            const corePulse = Math.max(0.1, 0.94 + Math.sin(now * 0.005) * 0.06);
            ctx.beginPath();
            ctx.ellipse(pedestalX, pedestalY, Math.max(0.1, 68 * corePulse), Math.max(0.1, 18 * corePulse), 0, 0, Math.PI * 2);
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
            ctx.lineWidth = 1.2;
            ctx.stroke();
            ctx.restore();

            // Concentric expanding floor shockwaves in styleHex
            for (let r = 0; r < 2; r++) {
              const ripplePhase = (((elapsed * 0.0012 + r * 0.5) % 1.0) + 1.0) % 1.0;
              const rx = Math.max(0.1, 32 + ripplePhase * 80);
              const ry = Math.max(0.1, rx * 0.27);
              ctx.save();
              ctx.beginPath();
              ctx.ellipse(pedestalX, pedestalY, rx, ry, 0, 0, Math.PI * 2);
              ctx.strokeStyle = styleHex;
              ctx.lineWidth = Math.max(0.2, 1.8 * (1 - ripplePhase));
              ctx.shadowColor = styleHex;
              ctx.shadowBlur = Math.max(0, 12 * (1 - ripplePhase));
              ctx.globalAlpha = Math.max(0, (1 - ripplePhase) * 0.75);
              ctx.stroke();
              ctx.restore();
            }

            const spriteDisplaySize = Math.min(w * 0.30, Math.min(h * 0.34, 130));
            const spriteX = Math.round(pedestalX - spriteDisplaySize * 0.5);
            const spriteY = Math.round(pedestalY - spriteDisplaySize + 16);
            const idleBob = Math.sin(now * 0.006) * 2;

            // 2. Procedural 3-Frame Ground Aura (Tailored pixel silhouette + wide celestial aura)
            if (isLootingVulture) {
              // Wide celestial ground aura spanning dragon's full length
              for (let f = 1; f <= 3; f++) {
                const fRadX = Math.max(0.1, (64 + f * 20) + Math.sin(elapsed * 0.005 + f) * 6);
                const fRadY = Math.max(0.1, (18 + f * 6) + Math.sin(elapsed * 0.005 + f) * 2.5);
                ctx.save();
                ctx.beginPath();
                ctx.ellipse(pedestalX, pedestalY, fRadX, fRadY, 0, 0, Math.PI * 2);
                ctx.strokeStyle = styleHex;
                ctx.lineWidth = 2.2;
                ctx.shadowColor = styleHex;
                ctx.shadowBlur = 16;
                ctx.stroke();
                ctx.restore();
              }
            }
            const frameIdx = Math.floor((now * 0.009) % 3);
            const auraEntry = buildAuraBitmap(unitType, race, 'western', styleHex, frameIdx);
            if (auraEntry && auraEntry.canvas) {
              const auraUnitSize = isLootingVulture ? Math.round(spriteDisplaySize * 0.92) : spriteDisplaySize;
              const auraSpriteX = Math.round(pedestalX - auraUnitSize * 0.5);
              const auraSpriteY = isLootingVulture
                ? Math.round(pedestalY - auraUnitSize * 0.88)
                : spriteY;
              const cellScreenSize = auraUnitSize / auraEntry.gridSize;
              const auraDrawW = (auraEntry.gridSize + auraEntry.padX * 2) * cellScreenSize;
              const auraDrawH = (auraEntry.gridSize + auraEntry.padTop + auraEntry.padBottom) * cellScreenSize;
              const auraDrawX = auraSpriteX - auraEntry.padX * cellScreenSize;
              const auraDrawY = (auraSpriteY + idleBob) - auraEntry.padTop * cellScreenSize;
              ctx.drawImage(auraEntry.canvas, auraDrawX, auraDrawY, auraDrawW, auraDrawH);
            } else if (!isLootingVulture) {
              // Procedural multi-tier aura fallback
              for (let f = 1; f <= 3; f++) {
                const fRad = Math.max(0.1, (38 + f * 16) + Math.sin(elapsed * 0.006 + f) * 5);
                ctx.save();
                ctx.beginPath();
                ctx.ellipse(pedestalX, pedestalY, fRad, Math.max(0.1, fRad * 0.28), 0, 0, Math.PI * 2);
                ctx.strokeStyle = styleHex;
                ctx.lineWidth = 2.2;
                ctx.shadowColor = styleHex;
                ctx.shadowBlur = 14;
                ctx.stroke();
                ctx.restore();
              }
            }

            // 3. Unit Live Pixel Sprite
            this._drawLiveUnit(ctx, {
              unitType,
              race,
              unitName,
              cardData,
              unitImage: result.unit?.image,
              pedestalX,
              pedestalY,
              drawSize: isLootingVulture ? Math.round(spriteDisplaySize * 0.92) : spriteDisplaySize,
              idleBob,
              isFiring: false,
              cycleTime: 0,
              now,
              elapsed,
              recoilX: 0
            });

            // 4. Rising Spark Motes
            ctx.fillStyle = styleHex;
            ctx.shadowColor = styleHex;
            ctx.shadowBlur = 10;
            const sparkSpread = isLootingVulture ? (spriteDisplaySize * 0.72) : (spriteDisplaySize * 0.44);
            for (let sp = 0; sp < 7; sp++) {
              const sparkPhase = (now * 0.0016 + sp * 0.14) % 1.0;
              const sparkY = (pedestalY + 6) - sparkPhase * (spriteDisplaySize * 1.05);
              const sparkX = pedestalX + Math.sin(now * 0.007 + sp * 2.3) * sparkSpread;
              const sparkAlpha = Math.sin(sparkPhase * Math.PI) * 0.95;
              ctx.globalAlpha = Math.max(0, sparkAlpha);
              ctx.fillRect(Math.floor(sparkX - 1.5), Math.floor(sparkY - 1.5), 3, 3);
            }
            ctx.globalAlpha = 1.0;

          } else if (attrId === 'bullet_color') {
            // ========================================================
            // ⚡ BULLET COLOR SHOWCASE: Runway Stage, Saturated Projectiles
            // ========================================================
            const pedestalX = Math.round(w * (isLootingVulture ? 0.31 : 0.25));
            const pedestalY = Math.round(h * 0.70);
            const targetX = Math.round(w * 0.82);
            const targetY = pedestalY - 26;

            const cycleDuration = 340; // ms per shot cycle
            const cycleTime = (((elapsed % cycleDuration) + cycleDuration) % cycleDuration);
            const isFiring = cycleTime < 75;
            const recoilP = isFiring ? Math.max(0, Math.min(1, cycleTime / 75)) : 0;
            const recoilX = isFiring ? -10 * Math.sin(recoilP * Math.PI) : 0;

            // 1. Pedestal under Unit (on Left)
            ctx.save();
            ctx.beginPath();
            ctx.ellipse(pedestalX, pedestalY, 74, 21, 0, 0, Math.PI * 2);
            ctx.strokeStyle = `${styleHex}aa`;
            ctx.lineWidth = 2;
            ctx.shadowColor = styleHex;
            ctx.shadowBlur = 14;
            ctx.stroke();
            ctx.fillStyle = `${styleHex}15`;
            ctx.fill();
            ctx.restore();

            // 2. Holographic Target Reticle (on Right)
            const droneBob = Math.sin(now * 0.0028) * 26 + Math.sin(now * 0.0056) * 6;
            const dY = targetY + droneBob;
            ctx.save();
            ctx.translate(targetX, dY);
            ctx.strokeStyle = `${styleHex}cc`;
            ctx.lineWidth = 1.8;
            ctx.shadowColor = styleHex;
            ctx.shadowBlur = 12;
            const rot = now * 0.002;
            ctx.rotate(rot);
            ctx.strokeRect(-14, -14, 28, 28);
            ctx.rotate(-rot);
            ctx.fillStyle = styleHex;
            ctx.beginPath();
            ctx.arc(0, 0, 3, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();

            // 3. Live Sprite with Spring Recoil & Flash Frame
            const spriteDisplaySize = Math.min(w * 0.30, 130);
            const drawResult = this._drawLiveUnit(ctx, {
              unitType,
              race,
              unitName,
              cardData,
              unitImage: result.unit?.image,
              pedestalX,
              pedestalY,
              drawSize: isLootingVulture ? Math.round(spriteDisplaySize * 0.88) : spriteDisplaySize,
              idleBob: 0,
              isFiring,
              cycleTime,
              now,
              elapsed,
              recoilX,
              targetX,
              targetY: dY,
              styleHex
            });

            // 4. Muzzle Calculation
            let muzzleX = drawResult.muzzleX;
            let muzzleY = drawResult.muzzleY;
            if (!isLootingVulture && !isPreserverLock && !drawResult.isArticulated) {
              const spriteX = pedestalX - spriteDisplaySize * 0.5 + recoilX;
              const spriteY = pedestalY - spriteDisplaySize + 16;
              if (isTesla) {
                muzzleX = spriteX + spriteDisplaySize * 0.64;
                muzzleY = spriteY + spriteDisplaySize * 0.36;
              } else if (isShade) {
                muzzleX = spriteX + spriteDisplaySize * 0.88;
                muzzleY = spriteY + spriteDisplaySize * 0.48;
              } else if (isRocketship) {
                muzzleX = spriteX + spriteDisplaySize * 0.82;
                muzzleY = spriteY + spriteDisplaySize * 0.46;
              } else if (isOrchestra || isApostate || isDelvingPillar) {
                muzzleX = spriteX + spriteDisplaySize * 0.56;
                muzzleY = spriteY + spriteDisplaySize * 0.30;
              }
            }

            // 5. Muzzle Flash Flare (during first 75ms of shot)
            if (isFiring) {
              const flashProgress = Math.max(0, Math.min(1, cycleTime / 75));
              const flashFade = Math.max(0, 1.0 - flashProgress);
              ctx.save();
              ctx.strokeStyle = styleHex;
              ctx.lineWidth = Math.max(0.5, 2.5 * flashFade);
              ctx.shadowColor = styleHex;
              ctx.shadowBlur = 24;
              ctx.beginPath();
              ctx.moveTo(muzzleX - 18 * flashFade, muzzleY);
              ctx.lineTo(muzzleX + 28 * flashFade, muzzleY);
              ctx.stroke();

              ctx.fillStyle = styleHex;
              ctx.beginPath();
              ctx.arc(muzzleX, muzzleY, Math.max(0, 9 * flashFade), 0, Math.PI * 2);
              ctx.fill();

              ctx.fillStyle = '#ffffff';
              ctx.beginPath();
              ctx.arc(muzzleX, muzzleY, Math.max(0, 3.5 * flashFade), 0, Math.PI * 2);
              ctx.fill();
              ctx.restore();
            }

            // 6. Weapon-Specific Authentic Projectiles in styleHex
            if (isPreserverLock) {
              // ⚡ Sul Preserver Lock: High-Energy Dual Lenses Plasma Beam in styleHex
              const beamProgress = Math.min(1.0, (cycleTime / cycleDuration) * 2.2);
              const reachX = muzzleX + beamProgress * (targetX - muzzleX);
              const reachY = muzzleY + beamProgress * (dY - muzzleY);
              const pulse = 0.8 + 0.2 * Math.sin(now * 0.03);

              ctx.save();
              // Layer 1: Radiant outer plasma bloom in styleHex
              ctx.strokeStyle = styleHex;
              ctx.shadowColor = styleHex;
              ctx.shadowBlur = 24;
              ctx.lineWidth = 6.0 * pulse;
              ctx.globalAlpha = 0.40;
              ctx.beginPath();
              ctx.moveTo(muzzleX, muzzleY);
              ctx.lineTo(reachX, reachY);
              ctx.stroke();

              // Layer 2: Main energetic laser core in styleHex
              ctx.strokeStyle = styleHex;
              ctx.lineWidth = 3.2 * pulse;
              ctx.globalAlpha = 0.90;
              ctx.beginPath();
              ctx.moveTo(muzzleX, muzzleY);
              ctx.lineTo(reachX, reachY);
              ctx.stroke();

              // Layer 3: Brilliant white inner ray
              ctx.strokeStyle = '#ffffff';
              ctx.lineWidth = 1.3 * pulse;
              ctx.globalAlpha = 0.95;
              ctx.beginPath();
              ctx.moveTo(muzzleX, muzzleY);
              ctx.lineTo(reachX, reachY);
              ctx.stroke();

              // Solar/plasma flare burst at twin lenses muzzle origin
              ctx.fillStyle = '#ffffff';
              ctx.beginPath();
              ctx.arc(muzzleX, muzzleY, 4.5 * pulse, 0, Math.PI * 2);
              ctx.fill();

              // Target impact spark corona in styleHex
              if (beamProgress >= 0.7) {
                ctx.fillStyle = styleHex;
                ctx.shadowColor = styleHex;
                ctx.shadowBlur = 14;
                ctx.beginPath();
                ctx.arc(reachX, reachY, 5 * pulse, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = '#ffffff';
                ctx.beginPath();
                ctx.arc(reachX, reachY, 2.5 * pulse, 0, Math.PI * 2);
                ctx.fill();

                for (let sp = 0; sp < 4; sp++) {
                  const spAng = (sp * Math.PI) / 2 + now * 0.02;
                  ctx.fillRect(reachX + Math.cos(spAng) * 9 - 1.5, reachY + Math.sin(spAng) * 9 - 1.5, 3, 3);
                }
              }
              ctx.restore();

            } else if (isLootingVulture) {
              // ☀️ Sul Looting Vulture: Apex Pyramid Solar Energy Beam in styleHex
              const beamProgress = Math.min(1.0, (cycleTime / cycleDuration) * 2.2);
              const reachX = muzzleX + beamProgress * (targetX - muzzleX);
              const reachY = muzzleY + beamProgress * (dY - muzzleY);

              ctx.save();
              // Outer solar plasma beam in styleHex
              ctx.strokeStyle = styleHex;
              ctx.shadowColor = styleHex;
              ctx.shadowBlur = 24;
              ctx.lineWidth = 5.0;
              ctx.beginPath();
              ctx.moveTo(muzzleX, muzzleY);
              ctx.lineTo(reachX, reachY);
              ctx.stroke();

              // Inner brilliant white core beam
              ctx.lineWidth = 1.8;
              ctx.strokeStyle = '#ffffff';
              ctx.beginPath();
              ctx.moveTo(muzzleX, muzzleY);
              ctx.lineTo(reachX, reachY);
              ctx.stroke();

              // Solar flare burst at apex origin
              ctx.fillStyle = '#ffffff';
              ctx.beginPath();
              ctx.arc(muzzleX, muzzleY, 5, 0, Math.PI * 2);
              ctx.fill();

              // Target impact sparks
              if (beamProgress >= 0.85) {
                ctx.fillStyle = styleHex;
                ctx.shadowColor = styleHex;
                ctx.shadowBlur = 14;
                for (let sp = 0; sp < 4; sp++) {
                  const spAng = (sp * Math.PI) / 2 + now * 0.02;
                  ctx.fillRect(reachX + Math.cos(spAng) * 10 - 1.5, reachY + Math.sin(spAng) * 10 - 1.5, 3, 3);
                }
              }
              ctx.restore();

            } else if (isTesla) {
              // ⚡ Tesla Dielectric Chain Lightning Arc in styleHex
              const arcT = Math.min(1.0, Math.max(0, cycleTime / 160));
              const reachX = muzzleX + arcT * (targetX - muzzleX);
              const reachY = muzzleY + arcT * (dY - muzzleY);
              ctx.save();
              for (let b = 0; b < 2; b++) {
                const segments = 7;
                ctx.strokeStyle = (b === 0) ? '#ffffff' : styleHex;
                ctx.lineWidth = (b === 0) ? 2.0 : 4.5;
                ctx.shadowColor = styleHex;
                ctx.shadowBlur = 22;
                ctx.beginPath();
                ctx.moveTo(muzzleX, muzzleY);
                for (let seg = 1; seg <= segments; seg++) {
                  const t = seg / segments;
                  const jitter = Math.sin(t * Math.PI) * (b === 0 ? 12 : 22);
                  const seed = Math.sin(now * 0.05 + seg * 13 + b * 27);
                  const nx = muzzleX + (reachX - muzzleX) * t + seed * jitter;
                  const ny = muzzleY + (reachY - muzzleY) * t + Math.cos(now * 0.04 + seg * 19) * jitter * 0.5;
                  ctx.lineTo(nx, ny);
                }
                ctx.stroke();
              }
              ctx.fillStyle = styleHex;
              ctx.shadowColor = styleHex;
              ctx.shadowBlur = 14;
              for (let sp = 0; sp < 4; sp++) {
                const spAng = (sp * Math.PI) / 2 + now * 0.02;
                ctx.fillRect(reachX + Math.cos(spAng) * 9 - 1.5, reachY + Math.sin(spAng) * 9 - 1.5, 3, 3);
              }
              ctx.restore();

            } else if (isShade) {
              // 🎯 Shade Hypersonic Rail Needle in styleHex
              const prog = Math.max(0, Math.min(1, cycleTime / cycleDuration));
              const bx = muzzleX + prog * (targetX - muzzleX);
              const by = muzzleY + prog * (dY - muzzleY);
              ctx.save();
              ctx.strokeStyle = styleHex;
              ctx.lineWidth = 4.0;
              ctx.shadowColor = styleHex;
              ctx.shadowBlur = 24;
              ctx.beginPath();
              ctx.moveTo(Math.max(muzzleX, bx - 65), by);
              ctx.lineTo(bx, by);
              ctx.stroke();

              ctx.strokeStyle = '#ffffff';
              ctx.lineWidth = 1.4;
              ctx.beginPath();
              ctx.moveTo(Math.max(muzzleX, bx - 55), by);
              ctx.lineTo(bx, by);
              ctx.stroke();
              ctx.restore();

            } else if (isRocketship || isOrchestra || isDelvingPillar) {
              // 🚀 Rocket Projectiles with exhaust plumes in styleHex
              for (let b = 0; b < 2; b++) {
                const bProg = (((elapsed + b * 170) % 340) + 340) % 340 / 340;
                const bx = muzzleX + bProg * (targetX - muzzleX);
                const by = muzzleY + bProg * (dY - muzzleY);

                ctx.save();
                for (let r = 0; r < 4; r++) {
                  const rp = (((elapsed * 0.005 + r * 0.25) % 1.0) + 1.0) % 1.0;
                  const rx = bx - 14 - rp * 24;
                  const ry = by + (Math.sin(r * 2 + now * 0.01) * 3);
                  ctx.fillStyle = styleHex;
                  ctx.shadowColor = styleHex;
                  ctx.shadowBlur = 14;
                  ctx.globalAlpha = Math.max(0, (1 - rp) * 0.85);
                  ctx.beginPath();
                  ctx.arc(rx, ry, Math.max(0, (1 - rp) * 4 + 1.5), 0, Math.PI * 2);
                  ctx.fill();
                }
                ctx.globalAlpha = 1.0;

                ctx.fillStyle = '#334155';
                ctx.beginPath();
                if (ctx.roundRect) ctx.roundRect(bx - 14, by - 3, 14, 6, 2);
                else ctx.rect(bx - 14, by - 3, 14, 6);
                ctx.fill();

                ctx.fillStyle = styleHex;
                ctx.shadowColor = styleHex;
                ctx.shadowBlur = 18;
                ctx.beginPath();
                ctx.moveTo(bx, by);
                ctx.lineTo(bx - 4, by - 3);
                ctx.lineTo(bx - 4, by + 3);
                ctx.closePath();
                ctx.fill();
                ctx.restore();
              }

            } else if (isApostate || isSulRace) {
              // 🧬 Sul / Apostate Spore Glob in styleHex
              for (let b = 0; b < 2; b++) {
                const bProg = (((elapsed + b * 170) % 340) + 340) % 340 / 340;
                const bx = muzzleX + bProg * (targetX - muzzleX);
                const by = muzzleY + bProg * (dY - muzzleY) - Math.sin(bProg * Math.PI) * 22;

                ctx.save();
                ctx.translate(bx, by);
                ctx.rotate(now * 0.01);
                ctx.fillStyle = styleHex;
                ctx.shadowColor = styleHex;
                ctx.shadowBlur = 20;
                ctx.beginPath();
                ctx.arc(0, 0, 5.5, 0, Math.PI * 2);
                ctx.arc(3, -2, 3.5, 0, Math.PI * 2);
                ctx.arc(-2.5, 2.5, 3.2, 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = '#ffffff';
                ctx.fillRect(-1, -1, 2, 2);
                ctx.restore();
              }

            } else {
              // 🎯 Standard / Marine / Infantry / Vehicles: Saturated Plasma Tracer Bolts in styleHex
              for (let b = 0; b < 2; b++) {
                const bProg = (((elapsed + b * 170) % 340) + 340) % 340 / 340;
                const bx = muzzleX + bProg * (targetX - muzzleX);
                const by = muzzleY + bProg * (dY - muzzleY) + (b % 2 === 0 ? -2 : 2);

                ctx.save();
                // 1. Trailing plasma velocity gradient streak in styleHex
                const tailGrad = ctx.createLinearGradient(bx - 36, by, bx, by);
                tailGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
                tailGrad.addColorStop(0.35, `${styleHex}44`);
                tailGrad.addColorStop(1, styleHex);
                ctx.strokeStyle = tailGrad;
                ctx.lineWidth = 5;
                ctx.shadowColor = styleHex;
                ctx.shadowBlur = 24;
                ctx.beginPath();
                ctx.moveTo(bx - 36, by);
                ctx.lineTo(bx, by);
                ctx.stroke();

                // 2. Main Plasma Capsule Body in styleHex!
                ctx.fillStyle = styleHex;
                ctx.shadowColor = styleHex;
                ctx.shadowBlur = 24;
                ctx.beginPath();
                if (ctx.roundRect) ctx.roundRect(bx - 16, by - 3.5, 20, 7, 3.5);
                else ctx.rect(bx - 16, by - 3.5, 20, 7);
                ctx.fill();

                // 3. Hot Inner Core Filament
                ctx.fillStyle = '#ffffff';
                ctx.globalAlpha = 0.9;
                ctx.beginPath();
                if (ctx.roundRect) ctx.roundRect(bx - 12, by - 1.2, 14, 2.4, 1.2);
                else ctx.rect(bx - 12, by - 1.2, 14, 2.4);
                ctx.fill();
                ctx.restore();
              }
            }

            // 7. Impact Bursts & Spark Detonations on Target in styleHex
            const hitCycle = (((elapsed % 170) + 170) % 170) / 170;
            if (hitCycle < 0.45) {
              const hitP = Math.max(0, Math.min(1, hitCycle / 0.45));
              const hitRadius = Math.max(0, 14 * hitP);
              ctx.save();
              ctx.strokeStyle = styleHex;
              ctx.lineWidth = Math.max(0.5, 2 * (1 - hitP));
              ctx.shadowColor = styleHex;
              ctx.shadowBlur = 18;
              ctx.beginPath();
              ctx.arc(targetX, dY, hitRadius, 0, Math.PI * 2);
              ctx.stroke();

              ctx.fillStyle = styleHex;
              ctx.shadowColor = styleHex;
              ctx.shadowBlur = 14;
              for (let sp = 0; sp < 6; sp++) {
                const spAng = (sp * Math.PI) / 3 + hitP * 1.5;
                const spDist = 8 + hitP * 24;
                const sx = targetX + Math.cos(spAng) * spDist;
                const sy = dY + Math.sin(spAng) * spDist;
                ctx.globalAlpha = Math.max(0, 1 - hitP);
                ctx.fillRect(sx - 1.5, sy - 1.5, 3, 3);
              }
              ctx.restore();
            }

          } else if (attrId === 'rocket_trail') {
            // ========================================================
            // 🚀 ROCKET TRAIL SHOWCASE: Authentic Arsenal-Grade Launch & Ballistic Contrail in styleHex
            // ========================================================
            const pedestalX = Math.round(w * 0.25);
            const pedestalY = Math.round(h * 0.70);
            const targetX = Math.round(w * 0.82);
            const targetY = pedestalY - 26;

            const cycleDuration = 1050; // ms per ballistic rocket flight cycle
            const cycleTime = (((elapsed % cycleDuration) + cycleDuration) % cycleDuration);
            const flightP = Math.min(1.0, cycleTime / 650); // 0..1 flight phase (first 650ms)
            const isFiring = cycleTime < 130;
            const recoilP = isFiring ? cycleTime / 130 : 0;
            const recoilX = isFiring ? -12 * Math.sin(recoilP * Math.PI) : 0;

            // 1. Pedestal under Unit (on Left)
            ctx.save();
            ctx.beginPath();
            ctx.ellipse(pedestalX, pedestalY, 74, 21, 0, 0, Math.PI * 2);
            ctx.strokeStyle = `${styleHex}aa`;
            ctx.lineWidth = 2;
            ctx.shadowColor = styleHex;
            ctx.shadowBlur = 14;
            ctx.stroke();
            ctx.fillStyle = `${styleHex}15`;
            ctx.fill();
            ctx.restore();

            // 2. Holographic Target Dummy Drone (on Right)
            const droneBob = Math.sin(now * 0.0028) * 26 + Math.sin(now * 0.0056) * 6;
            const dY = targetY + droneBob;
            ctx.save();
            ctx.translate(targetX, dY);
            ctx.strokeStyle = `${styleHex}cc`;
            ctx.lineWidth = 1.8;
            ctx.shadowColor = styleHex;
            ctx.shadowBlur = 12;
            const rot = now * 0.002;
            ctx.rotate(rot);
            ctx.strokeRect(-14, -14, 28, 28);
            ctx.rotate(-rot);

            // Crosshairs
            ctx.beginPath();
            ctx.moveTo(-18, 0); ctx.lineTo(-12, 0);
            ctx.moveTo(12, 0);  ctx.lineTo(18, 0);
            ctx.moveTo(0, -18); ctx.lineTo(0, -12);
            ctx.moveTo(0, 12);  ctx.lineTo(0, 18);
            ctx.stroke();

            // Center Target Core
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.arc(0, 0, 3, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();

            // 3. Render Live Firing Unit Sprite with Recoil
            const spriteDisplaySize = Math.min(w * 0.28, 120);
            const spriteX = pedestalX - spriteDisplaySize * 0.5;
            const spriteY = pedestalY - spriteDisplaySize + 12;

            this._drawLiveUnit(ctx, {
              unitType,
              race,
              unitName,
              cardData,
              unitImage: result.unit?.image,
              pedestalX,
              pedestalY,
              drawSize: isLootingVulture ? Math.round(spriteDisplaySize * 0.90) : spriteDisplaySize,
              idleBob: Math.sin(now * 0.0075) * 2.5,
              isFiring,
              cycleTime,
              now,
              elapsed,
              recoilX,
              isRocket: true
            });

            // 4. Weapon Muzzle Launch Positions
            let muzzleX = spriteX + spriteDisplaySize * 0.82;
            let muzzleY = spriteY + spriteDisplaySize * 0.50;
            if (isRocketship) {
              muzzleX = spriteX + spriteDisplaySize * 0.80;
              muzzleY = spriteY + spriteDisplaySize * 0.44;
            } else if (isOrchestra) {
              muzzleX = spriteX + spriteDisplaySize * 0.56;
              muzzleY = spriteY + spriteDisplaySize * 0.26;
            } else if (isDelvingPillar) {
              muzzleX = spriteX + spriteDisplaySize * 0.52;
              muzzleY = spriteY + spriteDisplaySize * 0.30;
            } else if (unitName === 'Pink Beret' || unitType === 'PinkBeret' || unitType === 'Pink_Beret') {
              muzzleX = spriteX + spriteDisplaySize * 0.82;
              muzzleY = spriteY + spriteDisplaySize * 0.32;
            } else if (unitName.includes('Vulture') || unitType.includes('Vulture')) {
              muzzleX = spriteX + spriteDisplaySize * 0.65;
              muzzleY = spriteY + spriteDisplaySize * 0.35;
            }

            // 5. Ignition / Launch Flash (first 100ms)
            if (cycleTime < 100) {
              const flashP = cycleTime / 100;
              const flashFade = 1.0 - flashP;
              ctx.save();
              ctx.fillStyle = styleHex;
              ctx.shadowColor = styleHex;
              ctx.shadowBlur = 24;
              ctx.beginPath();
              ctx.arc(muzzleX, muzzleY, Math.max(0, 10 * flashFade), 0, Math.PI * 2);
              ctx.fill();
              ctx.fillStyle = '#ffffff';
              ctx.beginPath();
              ctx.arc(muzzleX, muzzleY, Math.max(0, 4.5 * flashFade), 0, Math.PI * 2);
              ctx.fill();
              ctx.restore();
            }

            // 6. Flying Rocket Projectiles & Contrails in styleHex
            if (flightP < 1.0) {
              if (isOrchestra) {
                // 🚀 Orchestra: High Parabolic Ballistic Artillery Rocket
                const arcHeight = 65;
                const baseX = muzzleX + flightP * (targetX - muzzleX);
                const arcOffset = -Math.sin(flightP * Math.PI) * arcHeight;
                const baseY = muzzleY + flightP * (dY - muzzleY) + arcOffset;

                const nextP = Math.min(1.0, flightP + 0.04);
                const nextX = muzzleX + nextP * (targetX - muzzleX);
                const nextY = muzzleY + nextP * (dY - muzzleY) - Math.sin(nextP * Math.PI) * arcHeight;
                const angle = Math.atan2(nextY - baseY, nextX - baseX);

                // Dense expanding smoke contrail puffs in styleHex
                for (let s = 1; s <= 6; s++) {
                  const trailP = Math.max(0, flightP - s * 0.035);
                  const tx = muzzleX + trailP * (targetX - muzzleX);
                  const ty = muzzleY + trailP * (dY - muzzleY) - Math.sin(trailP * Math.PI) * arcHeight;
                  ctx.save();
                  ctx.fillStyle = styleHex;
                  ctx.shadowColor = styleHex;
                  ctx.shadowBlur = 12;
                  ctx.globalAlpha = Math.max(0, (1.0 - s * 0.15) * 0.75);
                  ctx.beginPath();
                  ctx.arc(tx, ty, 3.5 + s * 1.1, 0, Math.PI * 2);
                  ctx.fill();
                  ctx.restore();
                }

                // Heavy Rocket Fuselage
                ctx.save();
                ctx.translate(baseX, baseY);
                ctx.rotate(angle);
                ctx.fillStyle = '#64748b';
                ctx.fillRect(-7, -2.5, 13, 5);
                ctx.fillStyle = '#f1f5f9';
                ctx.fillRect(-7, -2.5, 13, 1.5);
                ctx.fillStyle = '#1e293b';
                ctx.fillRect(-7, -4.5, 3.5, 9); // Fins
                ctx.fillStyle = '#ef4444';
                ctx.fillRect(6, -2, 3.5, 4); // Warhead
                // Booster plume in styleHex
                ctx.fillStyle = styleHex;
                ctx.shadowColor = styleHex;
                ctx.shadowBlur = 14;
                ctx.fillRect(-11, -1.5, 4, 3);
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(-9, -0.75, 2, 1.5);
                ctx.restore();

              } else if (isDelvingPillar) {
                // 🔮 Delving Pillar: Crystalline Bio-Plasma Missile
                const baseX = muzzleX + flightP * (targetX - muzzleX);
                const baseY = muzzleY + flightP * (dY - muzzleY) + Math.sin(flightP * Math.PI * 2) * 5;

                // Glowing energy trail motes in styleHex
                for (let s = 1; s <= 5; s++) {
                  const tP = Math.max(0, flightP - s * 0.04);
                  const tx = muzzleX + tP * (targetX - muzzleX);
                  const ty = muzzleY + tP * (dY - muzzleY) + Math.sin(tP * Math.PI * 2) * 5;
                  ctx.save();
                  ctx.fillStyle = styleHex;
                  ctx.shadowColor = styleHex;
                  ctx.shadowBlur = 14;
                  ctx.globalAlpha = Math.max(0, (1.0 - s * 0.18) * 0.7);
                  ctx.beginPath();
                  ctx.arc(tx, ty, 3.2 + s * 0.8, 0, Math.PI * 2);
                  ctx.fill();
                  ctx.restore();
                }

                // Crystal Missile Sprite
                ctx.save();
                ctx.translate(baseX, baseY);
                ctx.rotate(now * 0.015);
                ctx.fillStyle = styleHex;
                ctx.shadowColor = styleHex;
                ctx.shadowBlur = 16;
                ctx.beginPath();
                ctx.moveTo(0, -6); ctx.lineTo(6, 0); ctx.lineTo(0, 6); ctx.lineTo(-6, 0);
                ctx.closePath();
                ctx.fill();
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(-1.5, -1.5, 3, 3);
                ctx.restore();

              } else if (unitName === 'Pink Beret' || unitType === 'PinkBeret' || unitType === 'Pink_Beret') {
                // 🚀 Pink Beret: 45° Target-Seeking RPG-7 Missile
                const arcHeight = 35;
                const baseX = muzzleX + flightP * (targetX - muzzleX);
                const arcOffset = -Math.sin(flightP * Math.PI) * arcHeight;
                const baseY = muzzleY + flightP * (dY - muzzleY) + arcOffset;

                const nextP = Math.min(1.0, flightP + 0.04);
                const nextX = muzzleX + nextP * (targetX - muzzleX);
                const nextY = muzzleY + nextP * (dY - muzzleY) - Math.sin(nextP * Math.PI) * arcHeight;
                const angle = Math.atan2(nextY - baseY, nextX - baseX);

                // Expanding smoke puffs with dual-color contrail in styleHex
                for (let s = 1; s <= 6; s++) {
                  const trailP = Math.max(0, flightP - s * 0.035);
                  const tx = muzzleX + trailP * (targetX - muzzleX);
                  const ty = muzzleY + trailP * (dY - muzzleY) - Math.sin(trailP * Math.PI) * arcHeight;
                  ctx.save();
                  ctx.fillStyle = (s % 2 === 0) ? styleHex : '#ffffff';
                  ctx.shadowColor = styleHex;
                  ctx.shadowBlur = 12;
                  ctx.globalAlpha = Math.max(0, (1.0 - s * 0.15) * 0.75);
                  ctx.beginPath();
                  ctx.arc(tx, ty, 3.2 + s * 1.0, 0, Math.PI * 2);
                  ctx.fill();
                  ctx.restore();
                }

                // RPG-7 Rocket Fuselage & Warhead
                ctx.save();
                ctx.translate(baseX, baseY);
                ctx.rotate(angle);

                // Body
                ctx.fillStyle = '#334155';
                ctx.fillRect(-8, -2.5, 14, 5);

                // Nosecone
                ctx.fillStyle = '#ffffff';
                ctx.beginPath();
                ctx.moveTo(6, -2.5);
                ctx.lineTo(11, 0);
                ctx.lineTo(6, 2.5);
                ctx.closePath();
                ctx.fill();

                // Decal stripe in styleHex
                ctx.fillStyle = styleHex;
                ctx.fillRect(-2, -2.5, 3, 5);

                // Booster flame
                const flameLen = 4 + Math.sin(now * 0.05) * 3;
                ctx.fillStyle = '#fde047';
                ctx.shadowColor = '#fde047';
                ctx.shadowBlur = 10;
                ctx.beginPath();
                ctx.moveTo(-8, -2);
                ctx.lineTo(-8 - flameLen, 0);
                ctx.lineTo(-8, 2);
                ctx.closePath();
                ctx.fill();
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(-9, -0.75, 2, 1.5);

                ctx.restore();

              } else if (unitName.includes('Vulture') || unitType.includes('Vulture')) {
                // 🦅 Enforcer Vulture: 2-Phase Ballistic Missile
                const apexH = 45;
                const ejectionFraction = 0.32;
                let curX, curY;
                if (flightP < ejectionFraction) {
                  const p1 = flightP / ejectionFraction;
                  curX = muzzleX + p1 * (targetX - muzzleX) * 0.15;
                  curY = muzzleY - apexH * (1.0 - Math.pow(1.0 - p1, 2.0));
                } else {
                  const p2 = (flightP - ejectionFraction) / (1.0 - ejectionFraction);
                  const startArcX = muzzleX + (targetX - muzzleX) * 0.15;
                  const startArcY = muzzleY - apexH;
                  curX = startArcX + p2 * (targetX - startArcX);
                  curY = startArcY + Math.pow(p2, 1.8) * (dY - startArcY);
                }

                // Contrail puffs in styleHex
                for (let s = 1; s <= 5; s++) {
                  const tP = Math.max(0, flightP - s * 0.04);
                  let tx, ty;
                  if (tP < ejectionFraction) {
                    const p1 = tP / ejectionFraction;
                    tx = muzzleX + p1 * (targetX - muzzleX) * 0.15;
                    ty = muzzleY - apexH * (1.0 - Math.pow(1.0 - p1, 2.0));
                  } else {
                    const p2 = (tP - ejectionFraction) / (1.0 - ejectionFraction);
                    const startArcX = muzzleX + (targetX - muzzleX) * 0.15;
                    const startArcY = muzzleY - apexH;
                    tx = startArcX + p2 * (targetX - startArcX);
                    ty = startArcY + Math.pow(p2, 1.8) * (dY - startArcY);
                  }
                  ctx.save();
                  ctx.fillStyle = styleHex;
                  ctx.shadowColor = styleHex;
                  ctx.shadowBlur = 12;
                  ctx.globalAlpha = Math.max(0, (1.0 - s * 0.18) * 0.7);
                  ctx.beginPath();
                  ctx.arc(tx, ty, 3.2 + s * 0.8, 0, Math.PI * 2);
                  ctx.fill();
                  ctx.restore();
                }

                // Missile body
                ctx.save();
                ctx.translate(curX, curY);
                ctx.fillStyle = styleHex;
                ctx.shadowColor = styleHex;
                ctx.shadowBlur = 16;
                ctx.beginPath();
                ctx.arc(0, 0, 5, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = '#ffffff';
                ctx.beginPath();
                ctx.arc(1.5, -1, 2, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();

              } else {
                // 🚀 Rocketship & Default: Dual Swarming Micro-Missiles with S-Curves
                for (let m = 0; m < 2; m++) {
                  const curveSign = (m === 0) ? -1 : 1;
                  const mOffsetP = m * 0.08;
                  const curFP = Math.min(1.0, Math.max(0, flightP - mOffsetP));
                  if (curFP <= 0 || curFP >= 1.0) continue;

                  const baseX = muzzleX + curFP * (targetX - muzzleX);
                  const baseY = muzzleY + curFP * (dY - muzzleY);
                  const arcOffset = Math.sin(curFP * Math.PI) * 22 * curveSign;
                  const curX = baseX;
                  const curY = baseY + arcOffset;

                  const nextFP = Math.min(1.0, curFP + 0.04);
                  const nextBaseX = muzzleX + nextFP * (targetX - muzzleX);
                  const nextBaseY = muzzleY + nextFP * (dY - muzzleY);
                  const nextArc = Math.sin(nextFP * Math.PI) * 22 * curveSign;
                  const angle = Math.atan2((nextBaseY + nextArc) - curY, nextBaseX - curX);

                  // Smoke trail puffs along curve in styleHex
                  for (let s = 1; s <= 5; s++) {
                    const tProg = Math.max(0, curFP - s * 0.04);
                    const tx = muzzleX + tProg * (targetX - muzzleX);
                    const ty = muzzleY + tProg * (dY - muzzleY) + Math.sin(tProg * Math.PI) * 22 * curveSign;
                    ctx.save();
                    ctx.fillStyle = styleHex;
                    ctx.shadowColor = styleHex;
                    ctx.shadowBlur = 12;
                    ctx.globalAlpha = Math.max(0, (1.0 - s * 0.18) * 0.65);
                    ctx.beginPath();
                    ctx.arc(tx, ty, 3 + s * 0.9, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.restore();
                  }

                  // Micro-missile sprite
                  ctx.save();
                  ctx.translate(curX, curY);
                  ctx.rotate(angle);
                  // Casing & fins
                  ctx.fillStyle = '#cbd5e1';
                  ctx.fillRect(-5, -2, 8, 4);
                  ctx.fillStyle = '#ffffff';
                  ctx.fillRect(-5, -2, 8, 1.2);
                  ctx.fillStyle = '#334155';
                  ctx.fillRect(-5, -3.5, 2.5, 7);
                  // Warhead
                  ctx.fillStyle = '#ef4444';
                  ctx.fillRect(3, -1.5, 3, 3);
                  // Thruster booster flame in styleHex
                  ctx.fillStyle = styleHex;
                  ctx.shadowColor = styleHex;
                  ctx.shadowBlur = 12;
                  ctx.fillRect(-7.5, -1, 2.5, 2);
                  ctx.fillStyle = '#ffffff';
                  ctx.fillRect(-6.5, -0.5, 1.5, 1);
                  ctx.restore();
                }
              }
            }

            // 7. Impact Shockwave & Sparks at Target Dummy (from 650ms to 900ms)
            if (cycleTime >= 650 && cycleTime < 920) {
              const impactProgress = (cycleTime - 650) / 270;
              const hitFade = Math.max(0, 1.0 - impactProgress);
              ctx.save();
              ctx.strokeStyle = styleHex;
              ctx.shadowColor = styleHex;
              ctx.shadowBlur = 20;
              ctx.lineWidth = Math.max(1, 2.8 * hitFade);
              ctx.beginPath();
              ctx.arc(targetX, dY, Math.max(0, 24 * impactProgress), 0, Math.PI * 2);
              ctx.stroke();

              // Radiating sparks in styleHex
              ctx.fillStyle = styleHex;
              ctx.shadowBlur = 14;
              for (let sp = 0; sp < 6; sp++) {
                const spAng = (sp * Math.PI) / 3 + impactProgress * 1.5;
                const spDist = 8 + impactProgress * 28;
                const sx = targetX + Math.cos(spAng) * spDist;
                const sy = dY + Math.sin(spAng) * spDist;
                ctx.globalAlpha = hitFade;
                ctx.fillRect(sx - 1.5, sy - 1.5, 3, 3);
              }
              ctx.restore();
            }

          } else {
            // General Unit Showcase Fallback
            const pedestalX = cx;
            const pedestalY = cy + 42;
            ctx.save();
            ctx.beginPath();
            ctx.ellipse(pedestalX, pedestalY, 78, 22, 0, 0, Math.PI * 2);
            ctx.strokeStyle = styleHex;
            ctx.lineWidth = 2.5;
            ctx.shadowColor = styleHex;
            ctx.shadowBlur = 16;
            ctx.stroke();
            ctx.fillStyle = `${styleHex}1a`;
            ctx.fill();
            ctx.restore();

            const spriteDisplaySize = Math.min(w * 0.28, 120);
            this._drawLiveUnit(ctx, {
              unitType,
              race,
              unitName,
              cardData,
              unitImage: result.unit?.image,
              pedestalX,
              pedestalY,
              drawSize: isLootingVulture ? Math.round(spriteDisplaySize * 0.90) : spriteDisplaySize,
              idleBob: 0,
              isFiring: false,
              cycleTime: 0,
              now,
              elapsed,
              recoilX: 0
            });

            ctx.fillStyle = styleHex;
            ctx.shadowColor = styleHex;
            ctx.shadowBlur = 8;
            for (let s = 0; s < 5; s++) {
              const spPhase = (elapsed * 0.0014 + s * 0.2) % 1.0;
              const spX = pedestalX + Math.sin(s * 2.5 + elapsed * 0.005) * 44;
              const spY = (pedestalY + 6) - spPhase * 75;
              const spAlpha = Math.sin(spPhase * Math.PI);
              ctx.globalAlpha = Math.max(0, spAlpha);
              ctx.fillRect(spX - 2, spY - 2, 4, 4);
            }
            ctx.globalAlpha = 1;
          }
        }
      }

      ctx.restore(); // Restores boot-in scale & alpha

      // ════════════════════════════════════════════════════════
      // 💥 CYBER-EMP DETONATION OVERLAYS (0ms to 480ms)
      // ════════════════════════════════════════════════════════
      if (elapsed < 480) {
        // 1. Central White-Hot EMP Flash & Radial Flare
        const flashFade = Math.max(0, 1.0 - (elapsed / 220));
        if (flashFade > 0) {
          const flashR = Math.min(w * 0.52, 210) * (0.5 + 0.5 * (1 - flashFade));
          const flashGrad = ctx.createRadialGradient(cx, cy, 2, cx, cy, Math.max(3, flashR));
          flashGrad.addColorStop(0, `rgba(255, 255, 255, ${0.98 * flashFade})`);
          flashGrad.addColorStop(0.2, `${empColor}${Math.round(255 * 0.9 * flashFade).toString(16).padStart(2, '0')}`);
          flashGrad.addColorStop(0.6, `${empColor}${Math.round(255 * 0.3 * flashFade).toString(16).padStart(2, '0')}`);
          flashGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
          ctx.fillStyle = flashGrad;
          ctx.beginPath();
          ctx.arc(cx, cy, flashR, 0, Math.PI * 2);
          ctx.fill();

          // Anamorphic horizontal laser streak blade
          const bladeFade = Math.max(0, 1.0 - (elapsed / 170));
          if (bladeFade > 0) {
            ctx.save();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = Math.max(0.5, 4.5 * bladeFade);
            ctx.shadowColor = empColor;
            ctx.shadowBlur = 24;
            ctx.beginPath();
            ctx.moveTo(0, cy);
            ctx.lineTo(w, cy);
            ctx.stroke();

            ctx.strokeStyle = empColor;
            ctx.lineWidth = Math.max(0.5, 9.0 * bladeFade);
            ctx.shadowBlur = 36;
            ctx.beginPath();
            ctx.moveTo(0, cy);
            ctx.lineTo(w, cy);
            ctx.stroke();
            ctx.restore();
          }
        }

        // 2. Dual Expanding Elliptical Neon Shockwaves
        // Shockwave 1: High-Speed Outer Shockwave (0..380ms)
        const shock1P = Math.min(1.0, elapsed / 380);
        if (shock1P < 1.0) {
          const ease1 = Math.sin(shock1P * Math.PI * 0.5);
          const rx1 = Math.max(1, ease1 * (w * 0.74));
          const ry1 = Math.max(1, ease1 * (h * 0.72));
          const shock1Alpha = (1.0 - shock1P) * 0.95;
          ctx.save();
          ctx.beginPath();
          ctx.ellipse(cx, cy, rx1, ry1, 0, 0, Math.PI * 2);
          ctx.strokeStyle = empColor;
          ctx.lineWidth = Math.max(0.5, 5.0 * (1.0 - shock1P));
          ctx.shadowColor = empColor;
          ctx.shadowBlur = Math.max(0, 32 * (1.0 - shock1P));
          ctx.globalAlpha = shock1Alpha;
          ctx.stroke();

          // Hot White Core Edge
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = Math.max(0.5, 1.8 * (1.0 - shock1P));
          ctx.stroke();
          ctx.restore();
        }

        // Shockwave 2: Trailing Resonant Shockwave (40..440ms)
        if (elapsed > 40 && elapsed < 440) {
          const shock2P = (elapsed - 40) / 400;
          const ease2 = Math.sin(shock2P * Math.PI * 0.5);
          const rx2 = Math.max(1, ease2 * (w * 0.58));
          const ry2 = Math.max(1, ease2 * (h * 0.56));
          const shock2Alpha = (1.0 - shock2P) * 0.65;
          ctx.save();
          ctx.beginPath();
          ctx.ellipse(cx, cy, rx2, ry2, 0, 0, Math.PI * 2);
          ctx.strokeStyle = empColor;
          ctx.lineWidth = Math.max(0.5, 3.0 * (1.0 - shock2P));
          ctx.shadowColor = empColor;
          ctx.shadowBlur = Math.max(0, 20 * (1.0 - shock2P));
          ctx.globalAlpha = shock2Alpha;
          ctx.stroke();
          ctx.restore();
        }

        // 3. Radial High-Velocity EMP Sparks & Digital Motes
        const sparkFade = Math.max(0, 1.0 - (elapsed / 440));
        if (sparkFade > 0) {
          for (const p of empParticles) {
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            p.vx *= Math.pow(p.drag, dt * 60);
            p.vy *= Math.pow(p.drag, dt * 60);

            const curSpeed = Math.hypot(p.vx, p.vy);
            const nX = curSpeed > 0.001 ? (p.vx / curSpeed) : 0;
            const nY = curSpeed > 0.001 ? (p.vy / curSpeed) : 0;
            const tailLen = Math.min(22, p.len * (curSpeed / 200));

            ctx.save();
            ctx.strokeStyle = p.color;
            ctx.lineWidth = Math.max(0.5, p.size * sparkFade);
            ctx.shadowColor = empColor;
            ctx.shadowBlur = 10;
            ctx.globalAlpha = sparkFade;
            ctx.beginPath();
            ctx.moveTo(p.x - nX * tailLen, p.y - nY * tailLen);
            ctx.lineTo(p.x, p.y);
            ctx.stroke();

            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.arc(p.x, p.y, Math.max(0.5, p.size * 0.45 * sparkFade), 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          }
        }
      }

      ctx.restore();
      this._holoAnimFrame = requestAnimationFrame(renderLoop);
    };

    this._holoAnimFrame = requestAnimationFrame(renderLoop);
  }
}
